/**
 * English UI strings. Every user-facing string lives here or in he.ts — never
 * inside a component (section 14.4). Keys are grouped by screen/feature.
 * `{name}` placeholders are interpolated by t().
 */
export const en = {
  // ---- app / navigation ----
  'app.name': 'Edri Family',
  'app.tagline': 'See your family, understand the connections, and preserve the stories behind the people.',
  'nav.tree': 'Family Tree',
  'nav.timeline': 'Timeline',
  'nav.me': 'My Profile',
  'nav.manage': 'Manage Family',
  'nav.admin': 'Admin controls',
  'nav.settings': 'Settings',
  'settings.advanced': 'Advanced mode',
  'admin.mode': 'Admin mode',
  'admin.mode.needed': 'Turn on Admin mode in the menu to manage the family.',
  'nav.notifications': 'Notifications',
  'nav.record': 'Record a Story',
  'nav.back': 'Back',
  'nav.close': 'Close',
  'nav.skip': 'Skip',
  'nav.next': 'Next',
  'nav.done': 'Done',
  'nav.cancel': 'Cancel',
  'nav.save': 'Save',
  'nav.continue': 'Continue',
  'nav.tryAgain': 'Try again',
  'nav.menu': 'Menu',
  'nav.viewingAs': 'Viewing as {name}',
  'lang.switch': 'עברית',
  'lang.switchLabel': 'Switch language to Hebrew',
  'lang.current': 'English',

  // ---- common ----
  'common.loading': 'Loading…',
  'common.saved': 'Saved',
  'common.saving': 'Saving…',
  'common.notBackedUp': 'Not yet backed up',
  'common.couldNotSave': 'Could not save',
  'common.offline': 'You are offline. Changes are kept on this device and will be sent when you reconnect.',
  'common.reconnected': 'Back online. Sending your saved changes…',
  'common.storageUnavailable': 'This browser\'s storage could not be opened, so photos and recordings saved on this device cannot be shown right now. Nothing is lost.',
  'common.reloadPage': 'Reload',
  'common.pendingCount': '{count} change(s) waiting to send',
  'common.retryNow': 'Send now',
  'common.discard': 'Discard',
  'common.remove': 'Remove',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.unknown': 'Unknown',
  'common.none': 'None',
  'common.optional': 'optional',
  'common.required': 'required',
  'common.search': 'Search',
  'common.clear': 'Clear',
  'common.more': 'More',
  'common.less': 'Less',
  'common.all': 'All',
  'common.confirm': 'Confirm',
  'common.you': 'You',
  'common.by': 'by {name}',
  'common.at': 'at {time}',
  'common.error.title': 'Something went wrong',
  'common.nothingLost': 'Nothing has been lost.',
  'common.livingStatus.living': 'Living',
  'common.livingStatus.deceased': 'Deceased',
  'common.livingStatus.unknown': 'Unknown',
  'common.gender.female': 'Female',
  'common.gender.male': 'Male',
  'common.gender.other': 'Other',
  'common.gender.unspecified': 'Prefer not to say',
  'common.role.member': 'Member',
  'common.role.helper': 'Profile Helper',
  'common.role.editor': 'Family Editor',
  'common.role.admin': 'Administrator',
  'common.deceasedMark': 'of blessed memory',
  /* The mark itself, written after the name. Stays Hebrew in both languages —
     it is what a Jewish family writes, not a phrase to be translated. */
  'common.memorialMark': 'ז״ל',
  'common.person.unnamed': 'Unnamed',
  'common.years': '{n} years',
  'common.age': 'Age {n}',
  'common.ageAtDeath': 'Lived {n} years',
  'common.showMore': 'Show {n} more',

  // ---- errors ----
  'error.offline': 'You are offline. This will be sent when you reconnect.',
  'error.network': 'The connection dropped for a moment. Nothing was lost — please try again.',
  'error.unexpected': 'Something unexpected happened. Your information is still on this screen — please try again.',
  'error.storage': 'This device could not store the photo. Reload the page and try again — nothing already saved is affected.',
  'error.notSignedIn': 'Please sign in first.',
  'error.permission': 'You do not have permission to do that.',
  'error.editorOnly': 'Only a Family Editor can do this.',
  'error.adminOnly': 'Only an Administrator can do this.',
  'error.notFound': 'We could not find that. It may have been merged or removed.',

  // ---- welcome / access ----
  'welcome.title': 'Welcome to the Edri family',
  'welcome.subtitle': 'A private place for our people, our connections and our stories — in our own voices.',
  'welcome.enterCode': 'I have a family access code',
  'welcome.haveAccount': 'I already have an account',
  'welcome.haveInvite': 'I received a personal invitation',
  'welcome.haveTempCode': 'I have a code from a family administrator',
  'welcome.privacy': 'Everything here is private to the family.',
  'access.code.title': 'Enter your family access code',
  'access.code.help': 'A family member shared this code with you. It only opens the door — you will still sign in as yourself.',
  'access.code.label': 'Family access code',
  'access.code.submit': 'Open the family space',
  'access.code.invalid': 'That code is not recognised. Please check it with the person who sent it.',
  'access.code.expired': 'That code has expired. Ask a family administrator for a new one.',
  'access.code.exhausted': 'That code has been used too many times. Ask a family administrator for a new one.',
  'access.code.ok': 'Code accepted. Now choose how you would like to sign in.',
  'access.invite.title': 'Enter your invitation code',
  'access.invite.help': 'Your personal invitation connects your account to your place in the tree, so no duplicate is created.',
  'access.invite.label': 'Invitation code',
  'access.invite.invalid': 'That invitation is not recognised.',
  'access.invite.used': 'That invitation was already used. If that was not you, tell a family administrator.',
  'access.invite.expired': 'That invitation has expired. Ask the person who invited you to send a new one.',
  'access.invite.needDestination': 'Please enter an email or phone number.',
  'access.invite.ok': 'Invitation accepted — you will be connected to {name}.',
  'access.temp.title': 'Enter the code from your family administrator',
  'access.temp.help': 'This code is only for this device. You can choose a simple PIN afterwards so you stay signed in.',
  'access.temp.label': 'Device code',
  'access.temp.invalid': 'That code is not recognised or was revoked.',
  'access.temp.expired': 'That code has expired. Ask your family administrator for a new one.',
  'access.temp.submit': 'Continue',

  // ---- sign in ----
  'signin.title': 'Sign in',
  'signin.subtitle': 'Choose the way that suits you best.',
  'signin.method.email': 'Email and password',
  'signin.method.link': 'Email me a sign-in link',
  'signin.method.phone': 'Text me a code',
  'signin.method.temp': 'I have a device code',
  'signin.email': 'Email',
  'signin.password': 'Password',
  'signin.submit': 'Sign in',
  'signin.createInstead': 'New here? Create an account',
  'signin.signInInstead': 'Already have an account? Sign in',
  'signin.forgot': 'Forgot your password?',
  'signin.forgotHelp': 'In the real app you would receive a reset email. In this prototype any password works for the demo accounts.',
  'signin.create.title': 'Create your account',
  'signin.create.name': 'Your name',
  'signin.create.help': 'Just an email and a password — you’ll tell us your name in a moment.',
  'signin.create.submit': 'Create account',
  'signin.link.sent': 'We sent a sign-in link to {email}. In this prototype, press the button below to open it.',
  'signin.link.open': 'Open the sign-in link',
  'signin.link.send': 'Send me a link',
  'signin.phone': 'Phone number',
  'signin.phone.send': 'Send code',
  'signin.phone.sent': 'We sent a code to {phone}. For this prototype the code is {code}.',
  'signin.phone.code': 'Code from the text message',
  'signin.phone.verify': 'Verify',
  'signin.demo.title': 'Prototype: sign in as a demo family member',
  'signin.demo.help': 'Any password works. Pick a person to see the app through their eyes.',
  'signin.demo.thisTab': 'Only in this tab (for testing two people side by side)',
  'signin.demo.button': 'Sign in as {name}',
  'auth.invalidCredentials': 'We did not recognise that email and password.',
  'auth.noMembership': 'That account is not part of this family yet.',
  'auth.invalidEmail': 'Please enter a valid email address.',
  'auth.invalidPhone': 'Please enter a valid phone number.',
  'auth.invalidCode': 'That code is not correct.',
  'auth.weakPassword': 'Please choose a password with at least 4 characters.',
  'auth.emailExists': 'An account with that email already exists. Try signing in instead.',
  'auth.needAccessCode': 'Please enter the family access code or an invitation first.',

  // ---- PIN ----
  'pin.setTitle': 'Choose a PIN for this device',
  'pin.setHelp': 'A 4-digit PIN lets you unlock the app quickly on this device. It is not a password and works only here.',
  'pin.unlockTitle': 'Welcome back, {name}',
  'pin.unlockHelp': 'Enter your PIN to continue.',
  'pin.label': 'PIN',
  'pin.confirmLabel': 'Enter the PIN again',
  'pin.mismatch': 'The two PINs do not match.',
  'pin.invalid': 'Please use 4 to 6 digits.',
  'pin.wrong': 'That PIN is not right. Try again — nothing is lost.',
  'pin.noSession': 'This device is not signed in. Ask your family administrator for a new code.',
  'pin.unlock': 'Unlock',
  'pin.skip': 'Skip for now',
  'pin.forgot': 'Forgot the PIN? A family administrator can give you a new device code.',
  'pin.lockNow': 'Lock this device',
  'recovery.banner': 'You are signed in on this device only. Add an email so you can get back in from another device.',
  'recovery.add': 'Add an email',
  'recovery.later': 'Later',
  'recovery.title': 'Add a way to get back in',
  'recovery.help': 'Adding an email keeps everything as it is — your place in the tree, your stories and your permissions stay the same.',
  'recovery.done': 'Done. {email} is now linked to your account.',

  // ---- onboarding ----
  'onb.step': 'Step {n} of {total}',
  'onb.lang.title': 'Which language do you prefer?',
  'onb.lang.help': 'You can switch at any time from any screen.',
  'onb.name.title': 'What is your name?',
  'onb.name.given': 'First name',
  'onb.name.family': 'Family name',
  'onb.name.nickname': 'Nickname',
  'onb.name.nicknameHint': 'What the family calls you',
  'onb.name.previewHe': 'In Hebrew your name will appear as',
  'onb.name.previewEn': 'In English your name will appear as',
  'onb.name.fix': 'Change',
  'onb.name.previewHelp': 'Relatives who use the app in the other language will see this. It is only a guess — fix the spelling if it looks wrong.',
  'onb.birth.title': 'When were you born?',
  'onb.birth.help': 'A year is enough — add the month and day if you like. This helps us place you in the tree.',
  'onb.birth.year': 'Year of birth',
  'onb.birth.pick': 'Choose a year',
  'onb.birth.ratherNot': 'I’d rather not say',
  'onb.inTree.title': 'Are you already in the family tree?',
  'onb.inTree.help': 'Someone may have added you before you joined.',
  'onb.inTree.yes': 'Yes, I think so',
  'onb.inTree.no': 'No, I am new to the tree',
  'onb.inTree.notSure': 'I am not sure',
  'onb.match.title': 'Is this you?',
  'onb.match.help': 'We found people with a similar name. Pick yourself, or tell us you are not in the list.',
  'onb.match.none': 'None of these are me',
  'onb.match.likely': 'Looks like a match',
  'onb.match.allClaimed': 'Everyone in the tree already has an account. Choose “None of these are me” to add yourself.',
  'onb.match.thisIsMe': 'This is me',
  'onb.match.invited': 'Your invitation connects you to {name}.',
  'onb.relation.title': 'How are you connected?',
  'onb.relation.help': 'Tell us one person you are related to. An editor can add more later.',
  'onb.relation.iAm': 'I am the',
  'onb.relation.of': 'of',
  'onb.relation.child': 'child',
  'onb.relation.parent': 'parent',
  'onb.relation.partner': 'partner',
  'onb.relation.sibling': 'sibling',
  'onb.relation.pick': 'Choose a family member',
  'onb.review.title': 'Ready to enter the tree',
  'onb.review.name': 'Name',
  'onb.review.birth': 'Born',
  'onb.review.connection': 'Connection',
  'onb.review.claim': 'You will be linked to {name}',
  'onb.review.new': 'You will be added as the {relation} of {name}',
  'onb.review.newNoRel': 'You will be added as a new person. An editor can connect you later.',
  'onb.review.reviewNote': 'Family connections added by members are checked by a Family Editor before they appear to everyone.',
  'onb.enter': 'Enter the Family Tree',
  'onb.claimPending': 'Your request to be linked was sent to a Family Editor. You can explore the tree meanwhile.',

  // ---- tree ----
  'tree.title': 'Family Tree',
  'tree.centerMe': 'Center on Me',
  'tree.myBranch': 'My Branch',
  'tree.myBranch.help': 'Shows you, your parents, partners, siblings and all your descendants.',
  'tree.branchOf': '{name}’s branch',
  'tree.entireFamily': 'Entire Family',
  'tree.find': 'Find Someone',
  'tree.filters': 'Filters',
  'tree.editFamily': 'Edit Family',
  'tree.editFamily.on': 'Editing — tap a person to add a relative',
  'tree.zoomIn': 'Zoom in',
  'tree.zoomOut': 'Zoom out',
  'tree.zoomReset': 'Reset view',
  'tree.listView': 'List view',
  'tree.mapView': 'Tree view',
  'tree.noMe': 'You are not linked to a person in the tree yet.',
  'tree.noMe.action': 'Find myself',
  'tree.legend': 'How to read the tree',
  'tree.legend.parent': 'Parent – child',
  'tree.legend.partner': 'Partners',
  'tree.legend.former': 'Former partners',
  'tree.legend.other': 'Adoptive, step or guardian',
  'tree.legend.pending': 'Awaiting review',
  'tree.legend.memory': 'New stories and photos (larger = newer)',
  'tree.legend.you': 'Teal ring — that is you',
  'tree.legend.dim': 'Faded — outside the branch or the filter',
  'tree.people': '{n} people shown',
  'tree.dimmed': '{n} dimmed by filters',
  'tree.hidden': '{n} hidden by filters',
  'tree.pendingMarker': 'Awaiting review',
  'tree.newStory': 'New story',
  'tree.stories': '{n} stories',
  'tree.hint': 'Drag to move · pinch or scroll to zoom · tap a person to open',
  'tree.keyboardHint': 'Use Tab to move between people, Enter to open, arrow keys to pan, + and − to zoom.',
  'tree.generation': 'Generation {n}',
  'tree.list.title': 'People in view',
  'tree.list.help': 'The same people and connections as the tree, as a list.',
  'tree.list.parents': 'Parents',
  'tree.list.partners': 'Partners',
  'tree.list.children': 'Children',
  'tree.list.siblings': 'Siblings',
  'tree.notInBranch': 'Not in the current branch',
  'tree.card.viewProfile': 'View Profile',
  'tree.card.pending': 'This person is awaiting review by a Family Editor.',
  'tree.card.you': 'This is you',
  'tree.card.latest': 'Latest',
  'portrait.add': 'Add a photo',
  'portrait.change': 'Change photo',
  'portrait.saved': 'Photo saved. Every photo is kept — nothing is lost.',
  'portrait.failed': 'That photo could not be read. Try a different one.',
  'portrait.ownedByOther': 'Only the person this circle belongs to can change their photo.',
  'portrait.unclaimed': 'Nobody has claimed this circle yet — anyone in the family can add a photo.',
  'portrait.yours': 'This circle is yours — only you can change its photos.',
  'portrait.managed': 'You manage this profile, so you can change its photos.',
  'portrait.adminEdit': 'This circle belongs to its owner. As an administrator you can change its photo and details under Edit details.',
  'portrait.owned': 'This circle belongs to its owner; only they can change its photos.',
  'portrait.gallery': 'Photos of this person',
  'portrait.current': 'Shown in the tree',
  'portrait.use': 'Show this photo in the tree',
  'portrait.addedWhen': 'Added {when}',
  'portrait.take': 'Take a photo',
  'portrait.choose': 'Choose a picture',
  'portrait.camera.title': 'Take a photo',
  'portrait.camera.help': 'Centre your face in the frame, then press the button.',
  'portrait.camera.snap': 'Take photo',
  'portrait.camera.retake': 'Retake',
  'portrait.camera.use': 'Use this photo',
  'portrait.camera.denied': 'We could not open the camera. Check that the browser is allowed to use it, or choose a picture instead.',
  'portrait.camera.unsupported': 'This browser cannot use the camera here. Choose a picture instead.',
  'tree.showEntireFamily': 'Show the entire family',
  'tree.lost': 'Back to the family',
  'tree.star': 'Star — follow this person closely',
  'tree.unstar': 'Remove star',
  'tree.legend.star': 'Gold ring — someone you starred',
  'tree.legend.size': 'Bigger circle — something new happened recently',
  'tree.badge.story': 'A story',
  'tree.badge.photo': 'A new photo',
  'tree.badge.more': '{n} more',
  'tree.meUnconnected': 'You are not connected to the tree yet — add a relative',
  'tree.card.myProfile': 'My profile',
  'tree.lock.locked': 'This circle is locked. Only an administrator can change its details.',
  'tree.lock.lockedAdmin': 'Locked — only administrators can edit it. Tap to unlock.',
  'tree.lock.open': 'This circle is not locked — changes apply right away.',
  'tree.lock.openAdmin': 'Not locked — changes apply right away. Tap to lock it.',
  'tree.lock.owned': 'This circle belongs to its owner. Your changes become suggestions for an administrator.',
  'tree.lock.nowLocked': 'Locked. From now on, changes to this person are suggestions.',
  'tree.lock.nowOpen': 'Unlocked. Changes to this person apply right away.',

  // ---- find & filters ----
  'find.title': 'Find someone',
  'find.placeholder': 'Name in English or Hebrew, nickname…',
  'find.noResults': 'No one matches “{q}”. Try another spelling or a nickname.',
  'find.reason.nickname': 'nickname',
  'find.reason.previous': 'previous name',
  'find.reason.hebrew': 'Hebrew name',
  'find.reason.english': 'English name',
  'find.reason.name': 'name',
  'filters.title': 'Filters',
  'filters.age': 'Age',
  'filters.ageFrom': 'From age',
  'filters.ageTo': 'To age',
  'filters.ageHelp': 'Age today for living people, age at death for others.',
  'filters.ageOn': 'Show people around a certain age',
  'filters.ageAround': 'Around {n}',
  'filters.ageRange': 'ages {from} to {to}',
  'filters.ageSpan': 'How close',
  'filters.ageSpan.n': '± {n} years',
  'filters.status': 'Show',
  'filters.status.all': 'Everyone',
  'filters.status.living': 'Living',
  'filters.status.deceased': 'Deceased',
  'filters.withStories': 'Only people with stories',
  'filters.mode': 'People who do not match',
  'filters.mode.dim': 'Dim them (keeps connections visible)',
  'filters.mode.hide': 'Show only matches',
  'filters.reset': 'Clear filters',
  'filters.apply': 'Apply',
  'filters.active': '{n} filters on',

  // ---- profile ----
  'profile.title': 'Profile',
  'profile.myTitle': 'My Profile',
  'profile.myAccount': 'Account, devices & sign out',
  'profile.names': 'Names',
  'profile.name.en': 'English name',
  'profile.name.he': 'Hebrew name',
  'profile.name.nickname': 'Nickname',
  'profile.name.previous': 'Previous names',
  'profile.name.given': 'First name',
  'profile.name.middle': 'Middle name',
  'profile.name.family': 'Family name',
  'profile.name.missing': 'No {lang} name recorded',
  'profile.born': 'Born',
  'profile.died': 'Died',
  'profile.birthPlace': 'Place of birth',
  'profile.status': 'Status',
  'profile.gender': 'Gender',
  'profile.family': 'Family',
  'profile.parents': 'Parents',
  'profile.partners': 'Partners',
  'profile.children': 'Children',
  'profile.siblings': 'Siblings',
  'profile.grandparents': 'Grandparents',
  'profile.grandchildren': 'Grandchildren',
  'profile.stories': 'Stories',
  'profile.storiesEmpty': 'No stories with {name} yet. Be the first to record one.',
  'profile.events': 'Life events',
  'profile.managers': 'Who can manage this profile',
  'profile.managersHelp': 'Visible to editors and the people involved.',
  'profile.claimedBy': 'Signed in as this person: {name}',
  'profile.notClaimed': 'No account is linked to this person.',
  'profile.helper': '{name} (helper)',
  'profile.history': 'Change history',
  'profile.historyHelp': 'Visible to editors and administrators.',
  'profile.edit': 'Edit Details',
  'profile.editShort': 'Edit',
  'profile.suggest': 'Suggest a correction',
  'profile.addRelative': 'Add a Relative',
  'profile.recordFor': 'Record a story for {name}',
  'profile.hearStories': 'Hear Stories',
  'profile.locked': 'Some details are locked by an administrator: {reason}',
  'profile.lockedShort': 'Locked',
  'profile.pendingChange': 'A change you suggested is awaiting review.',
  'profile.relationToYou': '{relation}',
  'profile.thisIsYou': 'This is you',
  'profile.notes': 'Notes',
  'profile.merged': 'This record was merged into another person.',
  'profile.viewMerged': 'Open the current record',
  'profile.subtype.biological': 'biological',
  'profile.subtype.adoptive': 'adoptive',
  'profile.subtype.step': 'step',
  'profile.subtype.guardian': 'guardian',
  'profile.subtype.foster': 'foster',
  'profile.subtype.unspecified': '',
  'profile.subtype.married': 'married',
  'profile.subtype.partnered': 'partners',
  'profile.subtype.former': 'former',
  'profile.myDrafts': 'Your unfinished recordings',
  'profile.myRequests': 'Your suggestions',
  'profile.myDevices': 'Your devices',
  'profile.signOut': 'Sign out',
  'profile.signOutWarn': 'You have {n} recording(s) not yet backed up. If you sign out now they stay on this device but will not upload until you sign in again here.',
  'profile.signOutAnyway': 'Sign out anyway',
  'profile.helping': 'You help manage',

  // ---- edit person ----
  'edit.title': 'Edit {name}',
  'edit.suggestTitle': 'Suggest a correction for {name}',
  'edit.reviewNote': 'Because this changes an important detail about someone else, a Family Editor will check it before it appears.',
  'edit.directNote': 'Your changes are saved directly and recorded in the change history.',
  'edit.lockedNote': 'This circle is locked by an administrator. Your changes are sent as a suggestion; once approved, they appear on the tree.',
  'edit.ownedNote': 'This circle belongs to someone else. Your changes are sent as a suggestion for an administrator to approve.',
  'edit.lockedOnlyAdmin': 'This circle is locked by an administrator. Only administrators can change its details.',
  'edit.portrait.changeHelp': 'Tap the camera to take a photo or choose one from this device. It becomes the picture shown in the tree.',
  'edit.reason': 'Why is this change needed?',
  'edit.reasonHelp': 'Helps the editor understand — for example “Her passport says 1932.”',
  'edit.presence': '{name} is editing this person.',
  'edit.presenceMany': '{names} are editing this person.',
  'edit.save': 'Save changes',
  'edit.send': 'Send for review',
  'edit.sent': 'Sent to a Family Editor. You will be told when it is reviewed.',
  'edit.saved': 'Saved.',
  'edit.savedMerged': 'Saved. Someone else changed other details at the same time — both sets of changes were kept.',
  'edit.queued': 'Saved on this device. It will be sent when you are back online.',
  'edit.noChanges': 'Nothing changed.',
  'edit.deathDate': 'Date of death',
  'edit.livingQuestion': 'Is {name} living?',
  'edit.portrait': 'Portrait',
  'edit.portrait.change': 'Choose a photo',
  'edit.portrait.remove': 'Remove photo',
  'edit.portrait.help': 'Photos stay on this device in the prototype.',
  'person.sentForReview': 'Sent for review.',
  'person.locked': 'These details are locked by an administrator. You can still suggest a correction.',
  'person.error.nameRequired': 'Please enter at least a first name.',

  // ---- conflicts ----
  'conflict.title': 'Someone changed this while you were editing',
  'conflict.body': '{name} changed this information {when}. Nothing has been lost. Please choose which information is correct.',
  'conflict.current': 'Current information',
  'conflict.yours': 'Your information',
  'conflict.keepCurrent': 'Keep Current',
  'conflict.useMine': 'Use Mine',
  'conflict.askEditor': 'Ask an Editor',
  'conflict.askEditorHelp': 'Sends both versions to a Family Editor to decide.',
  'conflict.escalated': 'Both versions were sent to a Family Editor.',
  'conflict.applyChoices': 'Save my choices',
  'conflict.field': 'Field',
  'conflict.offlineTitle': 'A saved change needs your decision',
  'conflict.offlineBody': 'While you were offline, {name} changed {field}. Choose which to keep.',

  // ---- add relative ----
  'add.step.who': 'Who are you adding?',
  'add.step.how': 'How are they related?',
  'add.step.more': 'A few more details (optional)',
  'add.useExisting': 'Already in the tree? Find them',
  'add.useNew': 'Add somebody new instead',
  'add.alsoChildOfBlank': 'Also a child of',
  'add.relationLabel': 'They are {name}’s…',
  'add.relationLabelBlank': 'They are…',
  'add.needRelatedTo': 'Choose who they are related to.',
  'add.thisPerson': 'this person',
  'add.needName': 'Type their name to add them.',
  'add.needRelation': 'Choose how they are related.',
  'add.inviteWarnTitle': 'They will be emailed',
  'add.inviteWarnBody': 'As soon as you press the button below, an invitation goes to {email} and they can open the family straight away. Clear the field if you would rather add them quietly.',
  'add.parent': 'Parent',
  'add.partner': 'Partner',
  'add.child': 'Child',
  'add.sibling': 'Sibling',
  'add.pickExisting': 'Choose an existing person',
  'add.subtype.parent': 'Kind of parent (optional)',
  'add.subtype.partner': 'Kind of partnership (optional)',
  'add.partnerCurrent': 'Current partners',
  'add.partnerFormer': 'Former partners',
  'add.partnerStart': 'Together since',
  'add.partnerEnd': 'Until',
  'add.alsoChildOf': 'Also a child of {name}?',
  'add.sharedParents': 'Which parents do they share?',
  'add.dupTitle': 'This may already be the same person',
  'add.dupBody': 'We found someone similar already in the tree. Would you like to review the existing person instead of creating a duplicate?',
  'add.dupReview': 'Use {name}',
  'add.dupIgnore': 'No, this is a different person',
  'add.dupReason.name': 'similar name',
  'add.dupReason.birth': 'similar birth year',
  'add.dupReason.connected': 'already connected to {name}',
  'add.previewSentence.parent': '{new} will be a parent of {anchor}.',
  'add.previewSentence.child': '{new} will be a child of {anchor}.',
  'add.previewSentence.partner': '{new} and {anchor} will be partners.',
  'add.previewSentence.sibling': '{new} will be a sibling of {anchor}, sharing {parents}.',
  'add.previewSentence.coParent': '{new} will also be a child of {names}.',
  'add.submit': 'Add to the family',
  'add.submitReview': 'Send to a Family Editor',
  'add.done': '{name} was added.',
  'add.note': 'Note for the editor (optional)',
  'rel.error.self': 'A person cannot be their own relative.',
  'rel.error.cycle': 'That would make someone their own ancestor. Please check the people chosen.',
  'rel.error.exists': 'That connection already exists.',
  'rel.error.tooManyParents': 'This person already has several parents recorded. Please check with an editor.',
  'rel.error.duplicate': 'This looks like the same person as {name}. Review before adding.',
  'rel.error.noPerson': 'Please choose or add a person.',
  'rel.error.siblingNeedsParent': 'To add a sibling, {name} needs at least one parent in the tree. Add the parent first.',
  'rel.error.editorOnly': 'Only a Family Editor can change this directly. You can suggest the change instead.',
  'rel.locked': 'This connection is locked by an administrator.',

  // ---- sentence builder ----

  // ---- stories / timeline ----
  'stories.title': 'Stories',
  'photos.title': 'Photos',
  'photos.subtitle': 'What the family is up to — a photo of the day, a gathering, and the events coming up.',
  'photos.add': 'Share a photo',
  'photos.addTitle': 'Share a photo',
  'photos.addEvent': 'Add an event',
  'photos.addEventTitle': 'Add an event',
  'photos.eventTitle': 'What is the event?',
  'photos.eventTitleHint': 'Noa’s bat mitzvah, Passover at Savta’s…',
  'photos.eventNotes': 'Details',
  'photos.eventNotesHint': 'Where, what time, who is hosting',
  'photos.eventWhen': 'When is it?',
  'photos.eventPicture': 'Add a picture (optional)',
  'photos.needPictureOrEvent': 'Add a picture, or give the event a name.',
  'photos.editDetails': 'Edit details',
  'photos.react': 'React with an emoji',
  'tree.card.openStory': 'Open the story',
  'tree.card.storyPeekHelp': 'Open the story to listen, read the transcript, or add your own memory of it.',
  'photos.detailsSaved': 'Details saved.',
  'photos.inPhoto': 'In this photo',
  'photos.nobodyPinned': 'Nobody has been pinned to this photo yet.',
  'photos.pinMe': 'I’m in this photo',
  'photos.tagSomeone': 'Tag people in this photo',
  'photos.tagTitle': 'Who is in this photo?',
  'photos.tagEmpty': 'Nobody yet — add yourself, or start typing a name.',
  'photos.pinSomeone': 'Pin someone who is in the photo',
  'photos.pinSomeoneHint': 'Start typing a name',
  'photos.unpin': 'Remove {name} from this photo',
  'photos.share': 'Share with the family',
  'photos.caption': 'Caption',
  'photos.captionHint': 'Who, where, what was happening',
  'photos.isEvent': 'This photo is an event',
  'photos.isEventHelp': 'A wedding, a birth, a reunion — something the family marks. Events get a special mark, and later the app can remind everyone when one is coming up.',
  'photos.when': 'When was this?',
  'photos.eventMark': 'Event',
  'photos.empty': 'No photos yet. Share the first one.',
  'photos.emptyEvents': 'No event photos yet. Mark a photo as an event when you share it.',
  'photos.saved': 'Photo shared with the family.',
  'photos.savedEvent': 'Event shared with the family.',
  'photos.sharedBy': 'Shared by {name} · {when}',
  'stories.subtitle': 'Family memories in the voices of the people who lived them.',
  'stories.empty': 'No stories match. Try clearing the filters — or record the first one.',
  'stories.emptyFirst': 'No stories yet. Record the first one — a memory, a moment, a person.',
  'stories.emptyFiltered': 'Nothing matches. Clear the search or the filter to see every story.',
  'stories.searchShort': 'Search stories',
  'stories.inStory': 'In this story: {names}',
  'stories.order.timeline': 'By date',
  'timeline.born': '{name} was born',
  'timeline.born.m': '{name} was born',
  'timeline.born.f': '{name} was born',
  'stories.order.latest': 'Latest',
  'stories.updated': 'updated {when}',
  'stories.draft.unfinished': 'Unfinished recording',
  'stories.filter.all': 'All stories',
  'stories.filter.person': 'About',
  'stories.filter.storyteller': 'Told by',
  'stories.filter.branch': 'My branch only',
  'stories.filter.from': 'From year',
  'stories.filter.to': 'To year',
  'stories.filter.place': 'Place',
  'stories.filter.type': 'Type',
  'stories.filter.type.audio': 'Recordings',
  'stories.filter.type.text': 'Written',
  'stories.search': 'Search titles and transcripts',
  'stories.approxGroup': 'Approximate',
  'stories.approxOrder': 'Order within this period is approximate',
  'stories.drafts': 'Your unfinished recordings',
  'stories.draft.resume': 'Continue',
  'stories.draft.delete': 'Delete draft',
  'stories.draft.deleteConfirm': 'Delete this draft and its recording from this device? This cannot be undone.',
  'stories.draft.recordedFor': 'For {name}',
  'stories.uploads': 'Uploads',
  'story.untitled': 'Untitled story',
  'story.titlePending': 'Title to be added',
  'story.toldBy': 'Told by {name}',
  'story.attribution': 'Spoken by {speaker} • Uploaded by {uploader}',
  'story.contributors': 'With memories from {names}',
  'story.people': 'People in this story',
  'story.inThis': 'In this story',
  'photos.sharedByLabel': 'Shared by',
  'story.place': 'Place',
  'story.event': 'Around',
  'story.spokenByLabel': 'Spoken by',
  'story.uploadedByLabel': 'Uploaded by',
  'story.when': 'When',
  'story.approximate': 'approximate',
  'story.contribution': '1 recording',
  'story.playAll': 'Play all in order',
  'story.playStory': 'Play this story',
  'story.differentDate': '{name} remembers it as {date}',
  'story.transcript': 'Transcript',
  'story.transcript.pending': 'Transcript is being prepared…',
  'story.transcript.processing': 'Transcript is being prepared…',
  'story.transcript.failed': 'The transcript could not be prepared automatically. An editor can retry.',
  'story.transcript.unavailable': 'No transcript yet.',
  'story.transcript.empty': 'The transcript will appear here once the recording has been processed.',
  'story.transcript.ai': 'Prepared automatically — may contain mistakes.',
  'story.transcript.corrected': 'Corrected by a person.',
  'story.transcript.correct': 'Correct the transcript',
  'story.transcript.translationNote': 'This is a translation.',
  // ---- who may change a story (domain/storyEditing.ts) ----
  // It belongs to whoever recorded it. Everyone else suggests and they decide;
  // an administrator changes it directly, in the admin colour.
  'story.edit': 'Edit story',
  'story.editAsAdmin': 'Edit story (administrator)',
  'story.belongsTo': '{name} recorded this story, so changes go to them first.',
  'story.delete': 'Delete this story',
  'story.deleteConfirm': 'Delete this story? The recordings go with it. An administrator can put it back.',
  'story.suggest': 'Suggest a change',
  'story.suggest.help': 'Change what looks wrong and send it to whoever recorded the story. Nothing changes until they say yes.',
  'story.suggest.note': 'Anything you want to add',
  'story.suggest.notePlaceholder': 'Savta always said it was the year after…',
  'story.suggest.summary': 'You are suggesting: {what}',
  'story.suggest.send': 'Send the suggestion',
  'story.suggest.sentToast': 'Sent to {name}.',
  'story.suggest.waiting': '{n} waiting for you',
  'story.suggest.waitingHelp': 'Someone in the family thinks this needs changing. Nothing happens until you say so.',
  'story.suggest.yours': 'What you suggested',
  'story.suggest.from': 'From {name}',
  'story.suggest.sent': 'Waiting for an answer',
  'story.suggest.accept': 'Yes, change it',
  'story.suggest.decline': 'No, leave it',
  'story.suggest.withdraw': 'Take it back',
  'story.suggest.accepted': 'Changed.',
  'story.suggest.declined': 'Left as it was.',
  'story.suggest.withdrawn': 'Taken back.',
  'story.change.title': 'Call it \u201C{title}\u201D',
  'story.change.date': 'Say it happened {date}',
  'story.change.people': 'People in it: {names}',
  'story.error.suggestInstead': 'Only the person who recorded this story can change it. You can suggest a change instead.',
  'story.error.editDirectly': 'This is your story \u2014 change it directly.',
  'story.editDate': 'Change when it happened',
  'story.events': 'Related events',
  'story.removeContribution': 'Remove my recording',
  'story.removeContributionConfirm': 'Remove this recording from the story? Other recordings stay. An administrator can restore it.',
  'story.locked': 'This story is locked by an administrator.',
  'story.status.local': 'Saved on this device',
  'story.status.uploading': 'Uploading… {pct}%',
  'story.status.paused_retry': 'Upload paused — will retry',
  'story.status.action_needed': 'Action needed',
  'story.status.uploaded': 'Uploaded — backup pending',
  'story.status.backed_up': 'Safely backed up',
  'story.status.failed_permanent': 'Could not be uploaded',
  'story.status.retryIn': 'Retrying in {s}s',
  'story.status.retry': 'Retry now',
  'story.status.pause': 'Pause upload',
  'story.status.resume': 'Resume upload',
  'story.status.actionHelp': 'The upload failed several times ({error}). Your recording is safe on this device. Try again, or ask an administrator.',
  'story.player.play': 'Play',
  'story.player.pause': 'Pause',
  'story.player.unavailable': 'Audio is not available on this device (it was recorded elsewhere and not yet backed up).',
  'story.new': 'New',
  'story.notFound': 'This story is not available.',
  'story.dateChanged': 'Date updated.',

  // ---- record ----
  'record.title': 'Record a Story',
  'record.intro': 'Record a memory in your own voice — a story about someone in the family, a moment, a place. It stays within the family, so the people who come after us can hear it.',
  'record.step.title': 'Title',
  'record.step.record': 'Record',
  'record.step.when': 'When',
  'record.step.people': 'People & place',
  'record.step.review': 'Review',
  'record.whoSpeaks': 'Who is telling this story?',
  'record.whoSpeaksHelp': 'You can record on behalf of someone you help manage. Everyone will see who spoke and who uploaded.',
  'record.titleLabel': 'Give the story a simple title',
  'record.titlePlaceholder': 'For example: How Saba came to Israel',
  'record.titleLater': 'Help me title it later',
  'record.start': 'Record Story',
  'record.recording': 'Recording',
  'record.paused': 'Paused',
  'record.pause': 'Pause',
  'record.resume': 'Continue',
  'record.finish': 'Finish recording',
  'record.replay': 'Listen',
  'record.discard': 'Discard and record again',
  'record.discardConfirm': 'Discard this recording? It will be deleted from this device.',
  'record.savedOnDevice': 'Saved on this device',
  'record.elapsed': 'Elapsed',
  'memories.recentre': 'Back to the middle',
  'memories.openStory': 'Open the story',
  'memories.openRecipe': 'Open the recipe',
  'voice.title': 'Say something about this day',
  'voice.title.recipe': 'Say something about this recipe',
  'voice.help': 'A sentence, a blessing, what you remember. It is kept with the pictures.',
  'voice.start': 'Start recording',
  'voice.stop': 'Stop',
  'voice.save': 'Keep it',
  'voice.again': 'Again',
  'voice.saved': 'Recording added',
  'voice.note': 'Recording',
  'record.permissionTitle': 'We need permission to use the microphone',
  'record.permissionHelp': 'Your browser will ask once. If you said no by mistake, allow the microphone in the address-bar settings and try again.',
  'record.unsupportedTitle': 'Recording is not available here',
  'record.unsupported.insecure': 'Recording only works on a secure (https) address or on this computer. Open the app over https to record from a phone.',
  'record.unsupported.no_api': 'This browser cannot record audio. Try a current version of Chrome, Safari, Edge or Firefox.',
  'record.typeInstead': 'Type the story instead',
  'record.typeLabel': 'Write the memory',
  'record.whenTitle': 'When did this happen?',
  'record.whenHelp': 'Approximate is fine. We never turn a guess into an exact date.',
  'record.peopleTitle': 'Who is in this story?',
  'record.peopleHelp': 'Optional. Tap to add people.',
  'record.placeTitle': 'Where did it happen?',
  'record.placeHelp': 'Optional — a city, or anywhere else: a school, a beach, Savta’s kitchen.',
  'record.reviewTitle': 'Review and publish',
  'record.publish': 'Publish',
  'record.saveDraft': 'Save as private draft',
  'record.draftSaved': 'Your draft is saved on this device. You can come back to it from Stories.',
  'record.published': 'Published! Your recording is saved on this device and is now uploading.',
  'record.publishOffline': 'You are offline. Your recording is safe on this device — publish when you reconnect.',
  'record.backgroundNote': 'You can leave this screen. Uploads continue and you can check them under Stories.',
  'record.error.empty': 'There is no recording or text yet.',
  'record.error.notAllowedFor': 'You can only record for yourself or for people you help manage.',
  'record.resumeDraft': 'You have an unfinished recording',
  'record.resumeDraftHelp': 'It is saved on this device.',
  'record.resume.continue': 'Continue it',
  'record.resume.new': 'Start a new one',
  'record.duration': 'Length',
  'record.noMic': 'No microphone was found.',

  // ---- fuzzy date input ----
  'date.kind': 'How precise is the date?',
  'date.kind.exact': 'Exact date',
  'date.kind.month': 'Month and year',
  'date.kind.year': 'Year',
  'date.kind.about': 'About a year',
  'date.kind.decade': 'Decade',
  'date.kind.range': 'Between two years',
  'date.kind.relative': 'By someone’s age',
  'date.kind.before_after': 'Before or after an event',
  'date.kind.unknown': 'I don’t know',
  'date.day': 'Date',
  'date.month': 'Month',
  'date.year': 'Year',
  'date.fromYear': 'From',
  'date.toYear': 'To',
  'date.decade': 'Decade',
  'date.relativePerson': 'Who',
  'date.relativeAge': 'was about this age',
  'date.beforeAfter': 'Before or after',
  'date.before': 'Before',
  'date.after': 'After',
  'date.event': 'Event',
  'date.freeText': 'In your own words',
  'date.freeTextPlaceholder': 'e.g. “the summer before the wedding”',
  'date.preview': 'Shown as: {text}',

  // ---- people picker ----
  'picker.placeholder': 'Type a name…',
  'picker.selected': 'Selected',
  'picker.addPerson': 'Add {name}',

  // ---- manage ----
  'manage.title': 'Admin controls',
  'manage.help': 'Everything that needs an administrator, in one place. Whatever is done here is kept in the history.',
  'manage.exit': 'Back to browsing',
  'manage.section.review': 'Awaiting review',
  'manage.section.duplicates': 'Possible duplicates',
  'manage.section.access': 'Invitations & codes',
  'manage.section.roles': 'Accounts & helpers',
  'manage.section.locks': 'Locked records',
  'manage.section.moderation': 'Stories & recipes',
  'manage.section.jobs': 'Uploads & processing',
  'manage.section.audit': 'History & restore',
  'manage.section.tree': 'Tree display',
  'manage.section.prototype': 'Prototype controls',
  'manage.tree.help': 'A circle grows when something new happens to a person — a new circle, a story, a photo — and settles back over time. The badges on its rim do the same. Set how big, how small, and how long.',
  'manage.tree.nodes': 'Circles',
  'manage.tree.nodeDefault': 'Default size',
  'manage.tree.nodeMax': 'Size when something is new',
  'manage.tree.nodeDays': 'Days to settle back',
  'manage.tree.badges': 'Badges on the rim',
  'manage.tree.badgeMax': 'Size when new',
  'manage.tree.badgeMin': 'Settled size',
  'manage.tree.badgeDays': 'Days to shrink',
  'manage.tree.globe': 'Circles on the globe',
  'manage.tree.globeHelp': 'On the World map the circles grow as you zoom in and shrink as you zoom out. Set how small they get at world zoom and how big at the closest zoom.',
  'manage.tree.globeMin': 'Size zoomed all the way out',
  'manage.tree.globeMax': 'Size zoomed all the way in',
  'manage.tree.days': '{n} days',
  'manage.tree.saved': 'Tree display saved — this is now how the tree and the world map look for everyone in the family.',
  'manage.tree.scope': 'These are the family’s settings, not just yours: they apply to everyone, on the tree and on the world map, on every device.',
  'manage.tree.reset': 'Back to defaults',
  'manage.count': '{n} items',
  'review.approve': 'Approve',
  'review.reject': 'Reject',
  'review.ask': 'Ask a question',
  'review.askPlaceholder': 'What would you like to know?',
  'review.rejectReason': 'Tell {name} why (they will see this)',
  'review.requestedBy': 'Requested by {name} {when}',
  'review.onBehalf': 'on behalf of {name}',
  'review.reason': 'Reason',
  'review.question': 'Question from {name}',
  'review.answer': 'Answer',
  'review.answerPlaceholder': 'Your answer…',
  'review.status.pending': 'Waiting',
  'review.status.approved': 'Approved',
  'review.status.rejected': 'Not applied',
  'review.status.question': 'Question asked',
  'review.approved': 'Approved.',
  'review.rejected': 'Marked as not applied.',
  'review.kind.relationship_add': 'New family connection',
  'review.kind.relationship_remove': 'Remove connection',
  'review.kind.relationship_change': 'Change connection',
  'review.kind.person_field': 'Correction',
  'review.kind.person_add': 'New person',
  'review.kind.claim': 'Account claim',
  'review.kind.merge': 'Merge',
  'review.kind.conflict_escalation': 'Editing conflict',
  'dup.help': 'Merging keeps every story, connection and account on one person. The other record stays in history.',
  'dup.confidence': '{pct}% likely',
  'dup.compare': 'Compare',
  'dup.notSame': 'Not the same person',
  'dup.merge': 'Merge…',
  'dup.keep': 'Keep this one',
  'dup.preview.title': 'Merge preview',
  'dup.preview.keep': 'Will be kept',
  'dup.preview.remove': 'Will be merged in',
  'dup.preview.relationships': '{n} connections move',
  'dup.preview.stories': '{n} stories move',
  'dup.preview.contributions': '{n} recordings move',
  'dup.preview.accounts': '{n} accounts move',
  'dup.preview.grants': '{n} helper grants move',
  'dup.preview.warn.status': 'The two records have different living status.',
  'dup.preview.warn.locked': 'One of the records has locked fields.',
  'dup.preview.warn.twoAccounts': 'Both records have accounts — the second claim will need review.',
  'dup.confirm': 'Merge these people',
  'dup.merged': 'Merged. The history keeps both identifiers.',
  'merge.error.same': 'Choose two different people.',
  'access.codes': 'Family access codes',
  'access.codes.help': 'Share sparingly. Prefer personal invitations after the first wave.',
  'access.codes.new': 'New code',
  'access.codes.label': 'Label',
  'access.codes.days': 'Valid for (days)',
  'access.codes.uses': '{uses} of {max} uses',
  'access.codes.expires': 'Expires {when}',
  'access.codes.revoked': 'Revoked',
  'access.codes.revoke': 'Revoke',
  'access.invites': 'Personal invitations',
  'access.invites.help': 'One-time, expiring, tied to one person — the safest way to bring someone in.',
  'access.invites.new': 'Invite someone',
  'access.invites.person': 'Person in the tree',
  'access.invites.destination': 'Email or phone',
  'access.invites.send': 'Create invitation',
  'access.invites.status.pending': 'Waiting',
  'access.invites.status.accepted': 'Accepted',
  'access.invites.status.expired': 'Expired',
  'access.invites.status.revoked': 'Revoked',
  'access.claims': 'Account claims',
  'access.claims.help': 'People who joined with the family code and asked to be linked to a person.',
  'access.temp': 'Temporary device access',
  'access.temp.adminHelp': 'For family members without email or phone. Each code is tied to one device and can be revoked.',
  'access.temp.new': 'Issue a device code',
  'access.temp.person': 'For',
  'access.temp.device': 'Device name',
  'access.temp.issue': 'Issue code',
  'access.temp.status.active': 'Active',
  'access.temp.status.revoked': 'Revoked',
  'access.temp.status.expired': 'Expired',
  'access.temp.notRedeemed': 'Not used yet',
  'access.temp.revoke': 'Revoke',
  'roles.help': 'Everyone with an account, whose circle they are signed in as, and what they may do.',
  'roles.change': 'Change role',
  'roles.error.lastAdmin': 'This is the last administrator. Make someone else an administrator first.',
  'roles.helpers': 'Profile helpers',
  'roles.helpersHelp': 'A helper manages specific people — never a whole branch — without sharing passwords.',
  'roles.grant': 'Add a helper',
  'roles.grant.helper': 'Helper (account)',
  'roles.grant.person': 'Manages',
  'roles.grant.scope': 'What they can do',
  'roles.scope.full': 'Edit profile and record stories',
  'roles.scope.record_only': 'Record stories only',
  'roles.grant.submit': 'Add helper',
  'roles.revoke': 'Remove',
  'roles.disconnect': 'Disconnect account from person',
  'locks.help': 'A lock does not freeze anything. While it is on, a change becomes a suggestion that an administrator approves — so nothing important is changed by accident.',
  'locks.unlock': 'Unlock',
  'locks.lock': 'Lock',
  'locks.reason': 'Reason (shown to family members)',
  'locks.fields': 'Which details?',
  'moderation.help': 'Lock a story so that changes to it come as suggestions, take a story or a recipe down, or put one back. Nothing is destroyed, and everything done here is recorded.',
  'moderation.archive': 'Take down',
  'moderation.restore': 'Put back',
  'moderation.lock': 'Lock',
  'moderation.unlock': 'Unlock',
  'moderation.archived': 'Taken down',
  'jobs.help': 'Recordings that could not be uploaded, transcribed or backed up.',
  'jobs.kind.upload': 'Upload',
  'jobs.kind.transcription': 'Transcription',
  'jobs.kind.backup': 'Backup',
  'jobs.kind.processing': 'Processing',
  'jobs.retry': 'Retry',
  'jobs.retried': 'Retry started.',
  'jobs.attempts': '{n} attempts',
  'audit.help': 'Every important change, who made it and when. Restoring creates a new compensating change — history is never erased.',
  'audit.restore': 'Restore previous values',
  'audit.restored': 'Restored.',
  'audit.error.notRestorable': 'This entry cannot be restored automatically.',
  'audit.filter': 'Filter by person',
  'audit.action.create': 'Created',
  'audit.action.update': 'Updated',
  'audit.action.archive': 'Removed',
  'audit.action.restore': 'Restored',
  'audit.action.approve': 'Approved',
  'audit.action.reject': 'Rejected',
  'audit.action.merge': 'Merged',
  'audit.action.lock': 'Locked',
  'audit.action.unlock': 'Unlocked',
  'audit.action.publish': 'Published',
  'audit.action.role_change': 'Role changed',
  'audit.action.grant': 'Access granted',
  'audit.action.revoke': 'Access revoked',
  'audit.action.sign_in': 'Signed in',
  'audit.action.claim': 'Claimed',
  'audit.before': 'Before',
  'audit.after': 'After',

  // ---- notifications ----
  'notif.title': 'Notifications',
  'notif.starred': 'Starred',
  'notif.empty': 'Nothing new.',
  'notif.markRead': 'Mark as read',

  // ---- settings ----
  'settings.title': 'Settings',
  'settings.language': 'Language',
  'settings.experience': 'Simple or Advanced',
  'settings.experience.help': 'Simple keeps the app to the family tree and adding things. Advanced opens up everything.',
  'settings.experience.simple': 'Simple',
  'settings.experience.simple.help': 'The family tree, and the Add button for photos, events and stories. Fewer buttons, less to learn.',
  'settings.experience.advanced': 'Advanced',
  'settings.experience.advanced.help': 'Everything: the World globe, Moments, the Stories tab, notifications and more.',
  'settings.adminTab': 'Admin controls',
  'settings.adminTab.show': 'Show Admin controls in the bottom bar',
  'settings.adminTab.help': 'Turn this off to hide the Admin controls tab. You stay an administrator and can turn it back on here any time.',
  'settings.theme': 'Appearance',
  'settings.theme.help': 'Dark is easier on the eyes in the evening; light is easier in bright daylight.',
  'settings.theme.dark': 'Dark',
  'settings.theme.light': 'Light',
  'settings.theme.system': 'Follow device',
  'settings.motion': 'Movement',
  'settings.motion.help': 'Turn off the gentle floating of people in the tree and large movements.',
  'settings.motion.system': 'Follow device setting',
  'settings.motion.reduce': 'Reduce movement',
  'settings.motion.full': 'Allow movement',
  'settings.textSize': 'Text size',
  'settings.textSize.normal': 'Normal',
  'settings.textSize.large': 'Larger',
  'settings.textSize.xlarge': 'Largest',
  'settings.account': 'Account',
  'settings.account.email': 'Email',
  'settings.account.phone': 'Phone',
  'settings.account.temporary': 'Signed in with a device code',
  'settings.devices': 'Devices',
  'settings.about': 'About this prototype',
  'settings.aboutText': 'A prototype: everything you do is stored in this browser only.',

  // ---- prototype panel ----
  'proto.title': 'Prototype',
  'proto.open': 'Prototype controls',
  'proto.help': 'Simulate real-world conditions to test the experience.',
  'proto.offline': 'Simulate offline',
  'proto.latency': 'Network delay',
  'proto.latency.none': 'None',
  'proto.latency.normal': 'Normal',
  'proto.latency.slow': 'Slow',
  'proto.latency.verySlow': 'Very slow',
  'proto.failures': 'Random save failures',
  'proto.failures.none': 'Off',
  'proto.failures.some': 'Some',
  'proto.failures.many': 'Many',
  'proto.uploadFailures': 'Unstable uploads',
  'proto.slowUploads': 'Slow uploads',
  'proto.otherEditor': 'Another person edits the open person',
  'proto.otherEditor.help': 'Open someone’s Edit screen first, then press this, then save — you will see the conflict flow.',
  'proto.otherEditor.done': '{by} just changed “{field}”. Now save your edit to see the conflict.',
  'proto.otherEditor.none': 'Open a person’s Edit Details screen first.',
  'proto.incomingStory': 'Someone publishes a story now',
  'proto.incomingStory.done': 'Leah just published a story. Watch her bubble in the tree.',
  'proto.secondTab': 'Open a second tab',
  'proto.secondTab.help': 'Sign in as a different person in that tab (this tab only) to test live updates and presence.',
  'proto.switchUser': 'Switch user (this tab only)',
  'proto.reset': 'Reset demo data',
  'proto.reset.confirm': 'Reset all demo data in this browser? Your recordings and changes will be deleted.',
  'proto.reset.done': 'Demo data reset.',
  'proto.export': 'Copy data as JSON',
  'proto.exported': 'Copied.',
  'proto.storage': 'Stored in this browser (IndexedDB)',
  'proto.pictures': 'Pictures in this browser',
  'proto.picturesCheck': 'Check the pictures',
  'proto.picturesCounts': 'Circle photos {portraits} · Pictures and clips {photos} · {size}',
  'proto.picturesAllOk': 'Every one of them reads back',
  'proto.picturesBroken': '{n} cannot be read',
  'proto.picturesOrphans': '{n} leftover files with nothing pointing at them',
  'proto.picturesRepair': 'Clear what is missing',
  'proto.picturesRepaired': 'Cleared {n} broken references and {t} leftover files',
  'proto.picturesMemory': 'Storage could not be opened — this session is running from memory',
  'proto.demoCodes': 'Demo codes',
  'proto.demoCodes.access': 'Family access code: {code}',
  'proto.demoCodes.temp': 'Miriam’s device code: {code}',
  'proto.demoCodes.invite': 'Nathalie’s invitation: {code}',
  'proto.demoCodes.phone': 'Phone code: {code}',
  // ---- where people live / World tab ----
  'nav.world': 'World',
  'nav.calendar': 'Calendar',
  'nav.passedDown': 'Passed Down',
  'calendar.title': 'The family’s days',
  'calendar.loading': 'Loading the timeline…',
  'calendar.hint': 'Drag sideways, or use the wheel · earlier to the left, coming up to the right · tap a day',
  'calendar.today': 'Today',
  'calendar.backToToday': 'Back to today',
  'calendar.isToday': 'Today',
  'calendar.yesterday': 'Yesterday',
  'calendar.inDays': 'In {n} days',
  'calendar.daysAgo': '{n} days ago',
  'calendar.empty': 'Nothing on the line yet — birthdays appear once birth dates are filled in, and events once someone shares one.',
  'calendar.filter.label': 'What to show',
  'calendar.view.label': 'Calendar view',
  'calendar.view.line': 'Days',
  'calendar.view.week': 'Week',
  'calendar.view.month': 'Month',
  'calendar.earlier': 'Earlier',
  'calendar.later': 'Later',
  'calendar.nothingOn': 'Nothing on this day.',
  'calendar.onDay': '{n} on this day',
  'calendar.filter.birthdays': 'Birthdays',
  'calendar.filter.events': 'Events',
  'calendar.filter.anniversaries': 'Anniversaries',
  'calendar.filter.yahrzeits': 'Yahrzeits',
  'calendar.filter.holidays': 'Chagim',
  'calendar.kind.birthday': 'Birthday',
  'calendar.kind.anniversary': 'Wedding anniversary',
  'world.title': 'Where the family lives',
  'world.hint': 'Drag to spin the globe · scroll or pinch to zoom · tap a circle',
  'world.centerMe': 'Where I am',
  'world.everyone': 'Everyone',
  'world.find': 'Find a person',
  'world.zoomIn': 'Zoom in',
  'world.zoomOut': 'Zoom out',
  'world.people': '{n} people here',
  'world.people.one': '1 person here',
  'world.inCity': 'In {city}',
  'world.nearby': 'Close together — zoom in to see everyone',
  'world.notOnMap': 'Not on the map yet',
  'world.notOnMap.help': 'These relatives have no home city yet.',
  'world.notOnMap.more': 'and {n} more',
  'world.addMine': 'Add where you live',
  'world.empty.title': 'Nobody is on the map yet',
  'world.empty.help': 'The World tab shows where everyone lives and who is visiting where. It fills up as relatives add their city.',
  'world.loading': 'Loading the globe…',
  'world.noData': 'Map outlines are not built yet — run node scripts/build-world-data.mjs.',
  'world.visiting': 'Visiting',
  'world.visitingUntil': 'Visiting {city} until {date}',
  'world.visitingFrom': 'In {city} from {from} to {to}',
  'world.legend': 'How to read the globe',
  'world.legend.home': 'A circle sits where the person lives',
  'world.legend.visit': 'A dotted ring: away on a visit right now',
  'world.legend.cluster': 'A number: that many people close together — zoom in',
  'world.legend.stem': 'A short line: nudged aside to make room — the dot is the real spot',
  'world.legend.sun': 'Sun and moon: where they are right now; the dark side is night',
  'world.legend.you': 'Teal ring: you · gold ring: people you starred',
  'world.data.credit': 'Map: Natural Earth · Cities: GeoNames',

  'places.livesInShort': 'Lives in',
  'places.noHome': 'No home city yet',
  'places.home': 'Home',
  'places.homeHelp': 'The family sees your city on the World tab. Nothing more precise unless you add it.',
  'places.search': 'Search for a city',
  'places.searchHint': 'Type a city name — e.g. Haifa, Toronto, Paris',
  'places.noResults': 'No city found. Try another spelling, or the nearest large city.',
  'places.searching': 'Searching…',
  'places.anywhereHint': 'Type a city — or anywhere at all',
  'places.useTyped': 'Use “{text}”',
  'places.change': 'Change',
  'places.clear': 'Remove',
  'places.precise': 'Be more precise (optional)',
  'places.preciseLabel': 'Neighbourhood or area',
  'places.preciseHelp': 'Shown only with the city, e.g. “Carmel, Haifa, Israel”. Leave empty to keep it at city level.',
  'places.picked': 'Chosen: {label}',
  'places.visits': 'Visits',
  'places.visitsHelp': 'Going somewhere? Add the dates and the map moves you there for the trip. Past visits stay in the record.',
  'places.addVisit': 'Add a visit',
  'places.visit.where': 'Where to?',
  'places.visit.from': 'First day',
  'places.visit.to': 'Last day',
  'places.visit.note': 'Note (optional)',
  'places.visit.noteHint': 'e.g. Passover with the family',
  'places.visit.save': 'Save the visit',
  'places.visit.saved': 'Visit added',
  'places.visit.cancel': 'Call it off',
  'places.visit.cancelled': 'Visit called off',
  'places.visit.cancelledMark': 'Called off',
  'places.visit.now': 'There now',
  'places.visit.upcoming': 'Coming up',
  'places.visit.past': 'Past',
  'places.visit.none': 'No visits planned.',
  'places.visitDenied': 'Only the person themselves (or someone helping with their profile) can add visits.',
  'places.invalidDate': 'Please choose both dates.',
  'places.endBeforeStart': 'The last day is before the first day.',
  'places.tooLong': 'A visit can be up to a year long — for longer, change the home city instead.',
  'places.inPast': 'Those dates are more than a year ago.',
  'places.record': 'Places',
  'places.record.help': 'Every home and visit since joining, newest first.',
  'places.record.since': 'since {date}',
  'places.record.until': 'until {date}',
  'places.record.range': '{from} – {to}',
  'places.homeSaved': 'Home city saved',
  'places.nudge': 'Add where you live',
  'places.nudgeHelp': 'Put yourself on the family map',
  'places.privacy': 'The family sees your city on the world map. Nothing more precise unless you add it.',

  'onb.home.title': 'Where do you live?',
  'onb.home.help': 'City is enough. This puts you on the family’s world map and lets the app know your local time.',
  'onb.home.skip': 'Skip for now',
  'onb.review.home': 'Lives in',

  'tree.legend.visit': 'A small suitcase: away on a visit right now',

  // ---- the Add tab ----
  'add.tab': 'Add',
  'add.sheetTitle': 'Add something',
  'add.subtitle': 'Share a photo, add an event, record a story — everything starts here.',
  'add.photo': 'Share a photo',
  'add.photoHelp': 'A snapshot, an old picture.',
  'add.event': 'Add an event',
  'add.eventHelp': 'A dinner, a trip, a get-together.',
  'add.story': 'Record a story',
  'add.storyHelp': 'In your voice, or written.',
  'add.relative': 'Add a relative',
  'photos.choosePicture': 'Choose a picture',
  'photos.changePicture': 'Choose a different picture',
  'photos.emptyHint': 'Use the + tab below to share the first one.',

  // ---- Shabbat ----
  'shabbat.now': 'Shabbat now',
  'shabbat.nowFor': 'It is Shabbat right now where {name} is',
  'shabbat.shalom': 'Shabbat shalom',
  'shabbat.quietTitle': 'Shabbat mode — notifications are waiting',
  'shabbat.quietBody': 'Shabbat mode is on. Anything that arrives during Shabbat will be here when it ends.',
  'tree.legend.shabbat': 'Two candles at the top: Shabbat right now where they are — teal for you, gold for a star, otherwise their own colour',
  'settings.gestures': 'Getting around',
  'settings.tapBack': 'Tap the background to go back',
  'settings.tapBack.help': 'In a circle of pictures, a tap on the empty space around them goes back — the same as the corner.',
  'settings.tabTips.reset': 'Show the tab descriptions again',
  'settings.tabTips.help': 'Each tab at the bottom says what it is for until you close its description with the ×. This brings all of them back.',
  'settings.tabTips.closed': 'Descriptions you have closed: {count}',
  'settings.tabTips.allShowing': 'All the descriptions are showing.',
  'settings.tabTips.done': 'Done — each tab will describe itself again.',
  // What each tab is for, said in a bubble over the dock until its × is pressed (app/tabTips.ts).
  'tabTip.close': 'Close this description',
  'tabTip.tree': 'Everyone in the family, as circles. Tap a circle to open that person — their pictures, stories and recipes float around them.',
  'tabTip.world': 'Where everyone lives, and who is visiting where. Spin the globe and tap a face.',
  'tabTip.timeline': 'The family’s stories, births and big days, back through the years. Tap one to open everything inside it.',
  'tabTip.calendar': 'What is coming up — birthdays, anniversaries, yahrzeits and the chagim — so nobody’s day is missed.',
  'tabTip.passedDown': 'Family recipes, know-how and secrets — kept here so they are never lost. Tap one to open it; press + to write one down.',
  'settings.shabbat': 'Shabbat',
  'settings.shabbat.mode': 'Shabbat mode',
  'settings.shabbat.help': 'From candle lighting to nightfall, where you are, the app keeps quiet.',
  'settings.shabbat.nowQuiet': 'It is Shabbat for you right now — notifications are being held.',
  'settings.shabbat.notNow': 'Not Shabbat right now. The app will go quiet at candle lighting.',

  // ---- family milestones (simchas) ----
  'add.milestone': 'Add a family milestone',
  'add.milestoneHelp': 'A wedding, a bar mitzvah, a new baby.',
  'photos.addMilestoneTitle': 'A family milestone',
  'photos.milestoneWhich': 'Mazal tov! What is the occasion?',
  'photos.milestoneChange': 'Change',
  'photos.milestoneTitle': 'Whose milestone, and what?',
  'photos.shareMilestone': 'Share the good news',
  'photos.savedMilestone': 'Shared with the family — mazal tov!',
  'photos.milestoneMark': 'Family milestone',
  'milestone.wedding': 'Wedding',
  'milestone.engagement': 'Engagement',
  'milestone.birth': 'A new baby',
  'milestone.brit': 'Brit / baby naming',
  'milestone.barMitzvah': 'Bar mitzvah',
  'milestone.batMitzvah': 'Bat mitzvah',
  'milestone.graduation': 'Graduation',
  'milestone.newHome': 'A new home',
  'milestone.aliyah': 'Aliyah',
  'milestone.anniversary': 'Anniversary',
  'milestone.memorial': 'In memory',
  'milestone.other': 'Something else',
  'milestone.hint.wedding': 'Noa and Tom’s wedding',
  'milestone.hint.engagement': 'Noa and Tom got engaged',
  'milestone.hint.birth': 'Welcome, baby Lior',
  'milestone.hint.brit': 'Lior’s brit',
  'milestone.hint.barMitzvah': 'Eitan’s bar mitzvah',
  'milestone.hint.batMitzvah': 'Noa’s bat mitzvah',
  'milestone.hint.graduation': 'Maya graduated from the Technion',
  'milestone.hint.newHome': 'The Katz family moved to Haifa',
  'milestone.hint.aliyah': 'Daniel made aliyah',
  'milestone.hint.anniversary': 'Savta and Saba’s 50th anniversary',
  'milestone.hint.memorial': 'Remembering Saba Yosef',
  'milestone.hint.other': 'What happened, and for whom',
  /* ---- Every day the family remembers is a place they can fill: a simcha, a story, a birth ---- */
  'memories.add': 'Add photos or videos',
  'memories.addShort': 'Add',
  'memories.adding': 'Adding {done} of {total}…',
  'memories.added': 'Added {n}',
  'memories.addFailed': 'Could not add {n} — too large, or not a picture or video',
  'memories.count': '{n} photos and videos',
  'memories.empty': 'Nothing here yet.',
  'memories.emptyHint': 'Add the first photos or videos from the day — everyone in the family can add theirs.',
  'memories.backToAll': 'All of it',
  'memories.video': 'Video',
  'nodes.details': 'Details',
  'nodes.addToPerson': 'Add to {name}',
  'nodes.recordStory': 'Record a story',
  'nodes.nothing': 'Nothing here yet.',
  'nodes.nothingHint': 'Their pictures, stories and recipes will float around here.',
  'tree.openCircle': 'Open {name}',
  /** What one picture is called when it is the only thing on the screen — the tab is "Photos"; this is one of them. */
  'memories.photo': 'Photo',
  'filters.all': 'Select all',
  'filters.none': 'Clear',
  'filters.noneChosen': 'Nothing ticked, so nothing is showing.',
  'filters.show': 'Show',
  'photos.zoomIn': 'Larger pictures',
  'photos.zoomOut': 'Smaller pictures',
  'photos.kind.photo': 'Photos',
  'photos.kind.video': 'Videos',
  'photos.kind.voice': 'Recordings',
  'photos.kind.event': 'Events',
  'photos.kind.milestone': 'Milestones',
  'memories.partOf': 'Part of {name}',
  'memories.main': 'Main picture',
  'memories.makeMain': 'Make this the main picture',
  'memories.madeMain': 'This is now the main picture',
  'memories.birthTitle': '{name}’s early days',
  'memories.birthKind': 'A birth',
  'memories.storyTitle': 'Pictures from this story',
  'memories.storyHint': 'Nothing yet — add a picture or a clip from the day this story is about.',

  // ---- prototype: Shabbat simulation ----
  'proto.shabbat': 'Pretend it is Shabbat',
  'proto.shabbat.help': 'The candles normally follow the real sunset where each person is. Tick people here to light their candles now — for you, this also switches on Shabbat mode’s quiet hours. Untick to go back to the real clock.',
  'proto.shabbat.everyone': 'Shabbat for everyone right now',
  'proto.shabbat.people': 'Shabbat for these people',
  'proto.shabbat.clear': 'Clear',
  'proto.shabbat.real': 'Really Shabbat there right now',
  'proto.shabbat.status': 'Pretending for {n} · really Shabbat right now for {real}',

  // ---- latest strip: posted / tagged; tagging people in stories ----
  'tree.card.posted': 'Posted',
  'tree.card.tagged': 'Tagged in',
  'tree.card.postedEmpty': 'Nothing shared yet.',
  'tree.card.taggedEmpty': 'Not tagged in any photo or story yet.',
  'story.tag': 'Tag people',
  'story.tagEdit': 'Edit',
  'story.tagEmpty': 'Nobody has been tagged in this story yet.',
  'story.tagTitle': 'Who is in this story?',
  'story.tagHelp': 'Tag the people this story is about or who were there — it will then show up under “Tagged in” on their circle.',
  'story.tagPick': 'People in this story',
  'story.tagSaved': 'People in the story updated.',
  // ---- Coming up: birthdays, anniversaries, yahrzeits, chagim (derived from the tree) ----
  'occasion.comingUp': 'Coming up',
  'occasion.today': 'today',
  'occasion.tomorrow': 'tomorrow',
  'occasion.birthday': '{name} turns {n}',
  'occasion.birthdayNoAge': '{name}’s birthday',
  'occasion.anniversary': '{names}: {n} years married',
  'occasion.anniversaryNoYears': '{names}: wedding anniversary',
  'occasion.yahrzeit': '{name}’s yahrzeit · {hebrew}',
  'occasion.yahrzeitChip': 'Yahrzeit {hebrew} · {date}',
  'occasion.yahrzeitToday': 'Yahrzeit today',
  'occasion.yahrzeitLong': 'Yahrzeit on {hebrew} — this year on {date}',
  'tree.legend.occasion': 'A small cake, heart or candle on the left: a birthday, an anniversary or a yahrzeit today',
  'tree.arrange.age': 'Arrange by age',
  'tree.arrange.generations': 'Arrange by generation',
  'tree.legend.age': 'By age: the higher, the earlier they were born — a couple sits at their average; the years run down the left',
  'holiday.roshHashana': 'Rosh Hashana',
  'holiday.yomKippur': 'Yom Kippur',
  'holiday.sukkot': 'Sukkot',
  'holiday.simchatTorah': 'Simchat Torah',
  'holiday.chanukah': 'Chanukah',
  'holiday.tuBishvat': 'Tu BiShvat',
  'holiday.purim': 'Purim',
  'holiday.pesach': 'Pesach',
  'holiday.yomHaatzmaut': 'Yom HaAtzmaut',
  'holiday.lagBaomer': 'Lag BaOmer',
  'holiday.shavuot': 'Shavuot',
  'holiday.tishaBav': 'Tisha B’Av',
  'holiday.greeting.shanaTova': 'Shana tova!',
  'holiday.greeting.gmar': 'Gmar chatima tova',
  'holiday.greeting.fast': 'An easy fast',
  'holiday.greeting.chag': 'Chag sameach!',

  // ---- Reach a relative: local time, WhatsApp / call / email ----
  'reach.message': 'Message on WhatsApp',
  'reach.whatsapp': 'WhatsApp',
  'reach.instagram': 'Instagram',
  'reach.instagramOpen': 'Open their Instagram',
  'reach.facebook': 'Facebook',
  'reach.facebookOpen': 'Open their Facebook',
  'reach.instagramHint': '@handle or a link',
  'reach.facebookHint': 'Name or a link',
  'reach.shabbatHint': 'it is Shabbat for them right now',
  'reach.section': 'How the family can reach you',
  'reach.help': 'A phone number or email, for a call or a WhatsApp message straight from your circle in the tree. Nothing is shown to anyone until you switch sharing on.',
  'reach.phone': 'Phone',
  'reach.phoneHint': '+972 52 …',
  'reach.emailLabel': 'Email',
  'reach.share': 'Share with the family',
  'reach.shareHelp': 'Relatives see a message button on this circle, and a button for each of the places above. Switch it off at any time.',

  // ---- Ask them something ----
  'ask.button': 'Ask {name} something',
  'ask.buttonShort': 'Ask',
  'ask.title': 'Ask {name} something',
  'ask.help': 'It waits for {name} in the bell and on their profile, and gets answered with a story.',
  'ask.label': 'Your question',
  'ask.placeholder': 'How did you meet Savta? What was the house in Casablanca like?',
  'ask.send': 'Send the question',
  'ask.sent': 'Sent — {name} will see it.',
  'ask.prompt.meet': 'How did you and your partner meet?',
  'ask.prompt.child': 'What do you remember from when you were small?',
  'ask.prompt.recipe': 'What is the dish that tastes like home, and how is it made?',
  'ask.prompt.advice': 'What is one thing you would like us to remember?',
  'ask.error.empty': 'Write the question first.',
  'ask.error.deceased': 'Questions can only be asked of the living — but you can record a memory about them.',
  'ask.section': 'Questions for {name}',
  'ask.askedBy': 'Asked by {name}',
  'ask.askedBySomeone': 'Asked by a family member',
  'ask.answer': 'Answer',
  'ask.dismiss': 'Set aside',
  'ask.dismissed': 'Question set aside.',
  'ask.answered': 'Answered',
  'ask.answeredWith': 'Answered with',
  'ask.alreadyAnswered': 'This question has already been answered — you can still record your own story.',
  'ask.alreadySetAside': 'This question was set aside — you can still record your own story.',
  'ask.notYours': 'This question is for {name}. Only they (or someone who records for them) can answer it — but your own memory is welcome as a story.',
  'notif.questionMark': 'Question',
  'notif.visitMark': 'Coming to town',
  'record.answering': 'Answering {name}’s question',
  'record.answeringSomeone': 'Answering a question',
  'story.answersQuestion': 'Told in answer to a question',
  'story.answersQuestionBy': 'Told in answer to {name}’s question',

  // ---- Reactions on stories, stories about a person ----
  'story.react': 'Your response',
  'story.react.add': 'Leave a reaction',
  'story.reacted': 'Sent.',
  'story.reactionRemoved': 'Taken back.',
  'profile.storiesTold': 'Told by {name}',
  'profile.storiesAbout': 'Stories about {name}',
  'profile.storiesAboutHelp': 'Stories in which they appear — told by others.',

  // ---- administrators, and how the family agrees on them ----
  'manage.section.admins': 'Administrators',
  'manage.section.decisions': 'Admin decisions',
  'admin.colourNote': 'Everything in this colour is an administrator control.',
  'admins.help': 'Administrators are named by email address. The list changes only when the administrators agree — nobody can add themselves.',
  'admins.current': 'Administrators now',
  'admins.active': 'Active',
  'admins.quiet': 'Not seen lately',
  'admins.notJoined': 'Has not signed in yet',
  'admins.lastSeen': 'Last active {when}',
  'admins.neverSeen': 'Nothing recorded yet',
  'admins.stepDown': 'Step down',
  'admins.stepDownHelp': 'You stop being an administrator and become a Family Editor. Another administrator can suggest you again later.',
  'admins.steppedDown': 'You are no longer an administrator.',
  'admins.suggest': 'Suggest an administrator',
  'admins.suggestHelp': 'A majority of the administrators who have been active recently has to approve — {n} of {total} right now. If nobody objects, it goes through on its own after {days} days.',
  'admins.suggestAction': 'Suggest',
  'admins.suggested': 'Suggested. The other administrators have been told.',
  'admins.email': 'Their email address',
  'admins.reason': 'Why (optional)',
  'admins.reasonPlaceholder': 'She already looks after the photos',
  'admins.openTitle': 'Waiting for the administrators',
  'admins.tally': '{yes} of {needed}',
  'admins.autoIn': 'Nobody has objected — it goes through in {days} days if that stays true.',
  'admins.needsFirstApproval': 'Waiting for the first approval.',
  'admins.objected': 'Objected to by {n}. It now needs the full majority.',
  'admins.approve': 'Approve',
  'admins.youApproved': 'You approved',
  'admins.object': 'Object',
  'admins.youObjected': 'You objected',
  'admins.objectHelp': 'Objecting stops this going through on its own. It can still pass if a majority approves.',
  'admins.voted': 'Recorded.',
  'admins.withdraw': 'Withdraw',
  'admins.withdrawn': 'Withdrawn.',
  'admins.settled': 'Settled',
  'admins.status.approved': 'Approved',
  'admins.status.declined': 'Declined',
  'admins.status.withdrawn': 'Withdrawn',
  'admins.status.open': 'Open',
  'admins.byMajority': 'A majority of the administrators approved',
  'admins.byTimeout': 'Nobody objected in time',
  'admins.rules': 'How the family decides',
  'admins.rulesHelp': 'Change these only with the other administrators. They are what stops a quiet month from freezing the family — and what stops one person deciding alone.',
  'admins.activeDays': 'An administrator counts towards a majority if active within',
  'admins.timeoutDays': 'An unopposed suggestion goes through after',
  'admins.vouchTitle': 'Approving new family members',
  'admins.vouchUnit': 'relatives have to say they know them',
  'admins.vouchHelp': 'Someone newly added is shown faintly in the tree until this many relatives confirm they know them — whoever added them counts as the first. Any administrator can approve someone at once, or change this number.',
  'admins.vouchFewer': 'Fewer relatives needed',
  'admins.vouchMore': 'More relatives needed',
  'admins.error.email': 'That does not look like an email address.',
  'admins.error.already': 'They are already an administrator.',
  'admins.error.openAlready': 'There is already a suggestion open for that address.',
  'admins.error.closed': 'That suggestion has already been settled.',

  // ---- what the administrators have decided ----
  'decisions.help': 'Everything done with administrator powers, so every administrator can see who did what, and when.',
  'decisions.by': '{name} · {when}',
  'decisions.empty': 'No administrator has done anything yet.',
  'decisions.removals': 'Removed pictures',
  'decisions.reason': 'Reason: {reason}',
  'decisions.noReason': 'No reason was given.',
  'decisions.reasonLabel': 'Reason (optional)',
  'decisions.reasonPlaceholder': 'Say why, if it helps the family understand',
  'decisions.reasonHelp': 'Whatever you write is kept with the decision and every administrator can read it.',
  'decisions.removePicture': 'Remove picture',
  'decisions.removePictureHelp': 'It stops being shown to the family. The picture is kept here, so this decision can be seen and undone.',
  'decisions.removed': 'Removed. It is in Admin decisions.',
  'decisions.putBack': 'Put back',
  'decisions.restored': 'Back where it was.',
  'audit.bySystem': 'The app',
  'audit.action.remove': 'Removed',

  // ---- recognising a newly added relative ----
  'recognise.waiting': 'Waiting for {n} more of the family to say they know them.',
  'recognise.waitingAny': 'Waiting for the family to recognise them.',
  'recognise.vouch': 'I know them',
  'recognise.mine': 'You said you know them',
  'recognise.thanks': 'Thank you — noted.',
  'recognise.approveNow': 'Approve now',
  'recognise.done': '{name} is part of the family tree.',

  // ---- adding a relative ----
  'add.relatedTo': 'Who are they related to?',
  'add.relatedToPlaceholder': 'Search the family',
  'add.inviteEmail': 'Their email (optional)',
  'add.inviteEmailPlaceholder': 'name@example.com',
  'add.inviteEmailHelp': 'We send them an invitation to join the family. Leave it blank and nothing is sent.',
  'add.recogniseNote': 'They join the tree straight away, shown faintly until enough of the family say they know them. Any administrator can approve them at once.',
  'add.doneInvited': '{name} was added, and an invitation is on its way to {email}.',
  'add.doneWaiting': '{name} was added — waiting for {n} of the family to say they know them.',
  'add.invite.badEmail': 'That does not look like an email address.',

  // ---- what a person has posted and is in ----
  'latest.filter': 'Show',
  'latest.filter.stories': 'Stories',
  'latest.filter.pictures': 'Pictures',

  'common.days': '{n} days',
  'common.save': 'Save',
  // ---- who looks after a circle ----
  'portrait.adminChange': 'Change this photo (administrator)',
  'profile.editAsAdmin': 'Edit details (administrator)',
  'edit.ownerOnly': 'Only the person who looks after this circle can change its details. An administrator can change anyone\u2019s.',
  'tree.unclaimed': 'Nobody has signed in as this person yet',
  'tree.legend.memorial': 'ז״ל: someone who has passed away, of blessed memory',
  'tree.legend.unclaimed': 'A dotted person: nobody has signed in as them yet',
  'owner.by': 'Looked after by {name}',
  'owner.you': 'You look after this circle',
  'owner.yourOwn': 'This is your own circle',
  'owner.themselves': '{name} looks after this circle',
  'owner.helpers': 'Also managed by {names}',
  'owner.nobody': 'Nobody looks after this circle yet',
  'owner.none': 'No keeper yet',
  'owner.change': 'Change who looks after it',
  'owner.changeHelp': 'Only the person who looks after a circle — and an administrator — can change its picture and details. Choose who that should be.',
  'owner.changed': 'Done.',
  'owner.clear': 'Leave it to nobody',
  'owner.claimedNote': 'They have claimed this circle themselves, so it stays theirs.',
  'owner.error.claimed': 'This person has claimed their own circle. Only they can change it.',

  // ---- removing a circle, by agreement between administrators ----
  'removal.propose': 'Propose removing',
  'removal.proposeHelp': 'The circle leaves the tree only once the other administrators agree. Nothing is destroyed — the person, their stories and this reason are kept, and it can be brought back.',
  'removal.proposed': 'Proposed. The other administrators have been told.',
  'removal.proposedBy': 'Proposed by {name}',
  'removal.pending': 'Waiting for the administrators to agree to remove this circle — {yes} of {needed}.',
  'removal.agree': 'Agree to remove',
  'removal.autoIn': 'Nobody has objected — it goes ahead in {days} days if that stays true.',
  'removal.objectHelp': 'Objecting stops this going ahead on its own. It can still happen if a majority agrees.',
  'removal.error.self': 'You cannot propose removing your own circle.',
  'removal.error.openAlready': 'There is already a proposal open for this circle.',

  // ---- the assistant: the Help button on every screen ----
  'assistant.title': 'Help',
  'assistant.open': 'Help',
  'assistant.greeting': 'What do you need?',
  'assistant.placeholder': 'Ask me anything',
  'assistant.send': 'Ask',
  'assistant.unsure': "I'm not sure about that one.",
  'assistant.showing': 'Showing you where to press',
  'assistant.gotIt': 'Got it',
  'assistant.cantShow': "I can't see that button from here.",
  'assistant.lookFor': 'Look for {what} — {where}.',
  'assistant.pressLit': 'Press the button that is lit up.',
  'assistant.by.local': 'Built in',
  'assistant.by.anthropic': 'Claude',
  'assistant.by.openai': 'OpenAI',
  'assistant.by.proxy': 'Your server',
  'assistant.age.some': 'Around your age: {who}.',
  'assistant.age.more': ' and {n} more',
  'assistant.age.none': 'Nobody else in the family has a year of birth close to yours.',
  'assistant.age.noMe': "I don't know which person in the tree you are, so I can't compare ages. Your profile can join you to your circle.",
  'assistant.age.noYear': "Your own year of birth isn't recorded, so there is nothing to compare it with.",
  'assistant.stopWalk': 'Stop showing me',
  'assistant.mode.simple': 'Simple',
  'assistant.limit.prototype': 'That part is not built yet.',
  'assistant.limit.design': "That's on purpose, not a fault.",
  'assistant.offerReport': "I can't fix that, but I can tell the family's administrators.",
  'assistant.did.went': 'Took you to {screen}',
  'assistant.did.opened': 'Opened {what}',
  'assistant.did.filled': 'Filled it in — check it and press Save',
  'assistant.did.mode': 'Switched to {mode} mode',
  'assistant.did.lang': 'Switched the language',
  'assistant.did.showedOnTree': 'Showed you {name} on the tree',
  'assistant.did.showedOnGlobe': 'Showed you where {name} is',
  'assistant.did.showedOnTimeline': 'Showing only {name} on the timeline',
  'assistant.did.showedOnCalendar': 'Found {name} on the calendar',
  'assistant.did.showedDay': 'Opened the calendar on {day}',

  // Voice
  'assistant.tapToTalk': 'Tap and ask',
  'assistant.orHold': 'or hold it while you talk',
  'assistant.letGo': 'Let go when you have finished',
  'assistant.working': 'Working on it…',
  'assistant.stopAll': 'Stop and close',
  'assistant.tapToStop': 'Tap when you have finished',
  'assistant.askAgain': 'Ask something else',
  'assistant.useKeyboard': 'Type it instead',
  'assistant.useVoice': 'Speak it instead',
  'assistant.listening': 'Listening…',
  'assistant.voice.denied': 'The microphone was not allowed. You can turn it on in your browser.',
  'assistant.voice.noMic': "I couldn't find a microphone.",
  'assistant.voice.failed': "The listening didn't work. You can type instead.",

  // Telling the administrators
  'assistant.report.kind.fault': 'Something is not working',
  'assistant.report.kind.idea': 'An idea for the app',
  'assistant.report.kind.question': 'A question for the administrators',
  'assistant.report.titleHint': 'In a few words, what happened?',
  'assistant.report.attached': 'Where you are and what you were doing is attached automatically.',
  'assistant.report.send': 'Send it',
  'assistant.report.sent': "Sent. The family's administrators can see it.",
  'assistant.report.needTitle': 'Tell me in a few words what happened.',

  // When it cannot answer
  'assistant.error.key': 'The assistant key was not accepted. An administrator can check it in Admin controls.',
  'assistant.error.rate': 'Too many questions at once. Try again in a moment.',
  'assistant.error.network': "I couldn't reach the assistant. Check the connection.",
  'assistant.error.server': 'The assistant is having trouble right now. Try again shortly.',
  'assistant.error.unknown': 'Something went wrong asking that.',
  'assistant.error.noKey': 'The assistant has not been set up yet. An administrator can do it in Admin controls.',
  'assistant.error.noUrl': 'The assistant has no address to call. An administrator can set one in Admin controls.',

  // Settings → Assistant (everyone)
  'settings.assistant': 'The Help button',
  'settings.assistant.listen': 'Let me speak instead of typing',
  'settings.assistant.listenHelp': 'Shows a microphone in the Help panel, so you can say what you need.',
  'settings.assistant.speak': 'Read answers out loud',
  'settings.assistant.speakHelp': 'Every answer is spoken as well as shown.',
  'settings.assistant.off': 'Voice is turned off for this family. An administrator can turn it on.',

  // Admin controls → From the family
  'manage.section.reports': 'From the family',
  'reports.title': 'Faults, ideas and questions',
  'reports.help': 'Sent from the Help button. Where the person was standing is attached — the screen, the mode, whether they were offline, and what last went wrong.',
  'reports.empty': 'Nothing yet.',
  'reports.from': 'From {name}',
  'reports.filter.all': 'All',
  'reports.kind.fault': 'Not working',
  'reports.kind.idea': 'Idea',
  'reports.kind.question': 'Question',
  'reports.status.new': 'New',
  'reports.status.seen': 'Read',
  'reports.status.planned': 'Planned',
  'reports.status.done': 'Done',
  'reports.status.declined': 'Not doing',
  'reports.context': 'Where they were',
  'reports.context.screen': 'Screen',
  'reports.context.mode': 'Mode',
  'reports.context.role': 'Role',
  'reports.context.online': 'Connection',
  'reports.context.online.yes': 'Online',
  'reports.context.online.no': 'Offline',
  'reports.context.pending': 'Waiting to be sent',
  'reports.context.lastError': 'Last error on screen',
  'reports.context.browser': 'Browser',
  'reports.context.assistant': 'Assistant',
  'reports.conversation': 'What they asked',
  'reports.answer': 'Answer them',
  'reports.answerHint': 'They see this in their notifications.',
  'reports.answerSend': 'Send the answer',
  'reports.answered': 'Answered {when}',
  'reports.saved': 'Saved.',

  // Admin controls → Assistant
  'manage.section.assistant': 'Assistant',
  'assistantAdmin.title': 'Who answers the Help button',
  'assistantAdmin.help': 'The assistant works with no setting at all — it knows this app and can walk anyone through it. Adding a model lets it answer in its own words and understand questions it has never been taught.',
  'assistantAdmin.provider': 'Who answers',
  'assistantAdmin.provider.local': 'Built in (no setup)',
  'assistantAdmin.provider.local.help': 'Answers the common questions and shows people where to press. Needs no key, works offline, and nothing leaves the device.',
  'assistantAdmin.provider.anthropic': 'Claude',
  'assistantAdmin.provider.openai': 'OpenAI, or anything that speaks its language',
  'assistantAdmin.provider.openai.help': 'Also works with Groq, Together, OpenRouter — and with a model on your own machine through Ollama or LM Studio, which needs no key at all.',
  'assistantAdmin.provider.proxy': 'Your own address',
  'assistantAdmin.provider.proxy.help': 'The app posts the question to an address you run, and your server holds the key. This is what to use once there is a backend.',
  'assistantAdmin.model': 'Model',
  'assistantAdmin.modelHint': 'Leave empty for {model}',
  'assistantAdmin.key': 'Key',
  'assistantAdmin.keyHint': 'Saved for the whole family — set it once here and nobody else has to.',
  'assistantAdmin.url': 'Address',
  'assistantAdmin.urlHint': 'The full address the question is posted to.',
  'assistantAdmin.urlOptional': 'Address (optional)',
  'assistantAdmin.urlOptionalHint': 'For a model on your own machine, e.g. http://localhost:11434/v1. Leave empty for the usual one.',
  'assistantAdmin.keyWarning': 'A key saved here can be read by any signed-in family member\u2019s browser, because every browser has to make the call. That is fine while testing with the family. Once real money is attached to the key, use your own address instead so it stays on a server.',
  'assistantAdmin.test': 'Test it',
  'assistantAdmin.testing': 'Testing…',
  'assistantAdmin.testOk': 'It answered. The assistant is working.',
  'assistantAdmin.testFailed': 'No answer: {why}',
  'assistantAdmin.why.credit': 'That key works, but its account has no credit on it. Buy credit in the Claude console — a key and a balance are two separate things, and a new key starts with none.',
  'assistantAdmin.why.model': 'There is no model by that name, or this key cannot use it. Empty the Model box to fall back to {model}.',
  'assistantAdmin.why.blocked': 'The request never left this browser. Usually no internet, an extension blocking it, or an address that is wrong.',
  'assistantAdmin.why.key': 'The key itself was refused. Copy it again from the console — it is one long line beginning sk-ant-, with no spaces or line breaks.',
  'assistantAdmin.why.serverKey': "The server's key was refused. Check OPENAI_API_KEY (or ANTHROPIC_API_KEY, for a Claude model) in the .env file or the hosting settings, then restart the app.",
  'assistantAdmin.server.answer': 'Answered by {name} ({model}). The key is kept on the server, so nobody in the family has anything to set up, on any device.',
  'assistantAdmin.server.keys': 'The models can be chosen here. The keys cannot: they live in the .env file on the computer running the app — or in the hosting settings — and changing one means editing it there and restarting.',
  'assistantAdmin.server.default': 'Server default ({model})',
  'assistantAdmin.server.unavailable': '{model} — not available with these keys, so the default is used',
  'assistantAdmin.server.modelsLoading': 'Loading the models these keys can use…',
  'assistantAdmin.server.modelHelp': 'Every model these keys can use. Smaller models (mini, nano, haiku) answer sooner and cost less; larger ones understand harder questions.',
  'assistantAdmin.server.effort': 'How long it thinks before answering',
  'assistantAdmin.server.effort.quick': 'Quickest',
  'assistantAdmin.server.effort.balanced': 'Balanced',
  'assistantAdmin.server.effort.careful': 'Most careful',
  'assistantAdmin.server.effortHelp': 'Every answer is waited for out loud, so Quickest is usually right. Most careful can add several seconds before anything is said.',
  'assistantAdmin.server.listenModel': 'Listening model',
  'assistantAdmin.server.speakModel': 'Voice model',
  'assistantAdmin.server.speechify': 'Speechify (English; Hebrew stays with OpenAI)',
  'assistantAdmin.listening': 'Who listens',
  'assistantAdmin.listen.off': 'Off',
  'assistantAdmin.listen.browser': "The browser's own",
  'assistantAdmin.listen.browser.help': 'Free, private, and works in Hebrew. Chrome and Safari only, and it shows the words as they are spoken.',
  'assistantAdmin.listen.openai': 'OpenAI Whisper',
  'assistantAdmin.listen.openai.help': 'Better than the browser with Hebrew and with an older voice. The recording is sent when they stop talking, so the words appear at the end rather than as they go.',
  'assistantAdmin.listen.proxy': 'Your own address',
  'assistantAdmin.listen.proxy.help': 'Posts the recording to /transcribe at the address below and expects {"text": "…"} back.',
  'assistantAdmin.listen.server': 'Always the server',
  'assistantAdmin.listen.server.help': 'Every question is recorded and sent to the server (OpenAI). Better with Hebrew and with an older voice, but the words appear when they stop talking rather than as they speak.',
  'assistantAdmin.listen.browser.serverHelp': 'The browser listens and shows the words as they are spoken — the quickest way. On a phone or browser that cannot, the server listens instead.',

  'assistantAdmin.speaking': 'Who speaks',
  'assistantAdmin.speakingHelp': 'The assistant reads every answer out loud, so this is the half most worth getting right.',
  'assistantAdmin.speak.off': 'Off',
  'assistantAdmin.speak.browser': "The browser's own",
  'assistantAdmin.speak.browser.help': 'Free and always there, but plain, and the Hebrew is poor on most machines.',
  'assistantAdmin.speak.speechify': 'Speechify',
  'assistantAdmin.speak.speechify.help': 'Warm, natural voices in many languages — the nicest of these to be read to by. Speaking only; something else does the listening.',
  'assistantAdmin.speak.speechify.where': 'From platform.speechify.ai → API keys.',
  'assistantAdmin.speak.openai': 'OpenAI',
  'assistantAdmin.speak.openai.help': 'Good voices, and the same key as Whisper if you are already using it.',
  'assistantAdmin.speak.proxy': 'Your own address',
  'assistantAdmin.speak.proxy.help': 'Asks /speak at the address below for audio, so the key stays on your server.',
  'assistantAdmin.speak.server': 'The server ({name})',
  'assistantAdmin.speak.server.help': 'A natural voice, with its key kept on the server. Answers play sentence by sentence, so the first words arrive quickly.',
  'assistantAdmin.speak.server.noHebrew': "Nothing on the server speaks Hebrew, so Hebrew answers use the browser's own voice. Add OPENAI_API_KEY to the server for a natural Hebrew voice.",
  'assistantAdmin.speak.server.hebrewEnding': "Speechify's current voices do not speak Hebrew. Its older model still does for now, but it is being switched off in November 2026 — add OPENAI_API_KEY to the server to keep a natural Hebrew voice.",
  'assistantAdmin.speakFallback': "If this voice cannot be reached — an expired key, a rate limit, or a browser refusing the call — the assistant falls back to the browser's own voice rather than going quiet.",
  'assistantAdmin.hear': 'Hear it',
  'assistantAdmin.hearing': 'Speaking…',
  'assistantAdmin.hearLine': 'Shalom — this is the voice the family will hear.',
  'assistantAdmin.heardOk': 'That is the voice. It is working.',
  'assistantAdmin.heardFailed': 'It could not speak. Check the key and the voice name above.',
  'assistantAdmin.log': 'What the assistant has been doing',
  'assistantAdmin.logHelp': 'The last calls it made — useful when an answer or a voice does not arrive and you need to see why.',
  'assistantAdmin.logCopy': 'Copy the log',
  'assistantAdmin.logCopied': 'Copied',
  'assistantAdmin.logClear': 'Clear',
  'assistantAdmin.logEmpty': 'Nothing yet. Ask the assistant something, or press Hear it.',
  'assistantAdmin.voiceNoList': 'No voices to choose from yet. Add the key above, or type a voice name.',
  'assistantAdmin.voiceName': 'Voice',
  'assistantAdmin.voiceDefault': 'Whichever the device uses',
  'assistantAdmin.voiceServerDefault': "The server's default voice",
  'assistantAdmin.cost': 'What each question costs',
  'assistantAdmin.costHelp': 'The app describes itself to the model in about {tokens} words of instructions. Nothing about the family is sent unless a question needs it, and then only a few lines.',
  'assistantAdmin.knows': 'What it knows',
  'assistantAdmin.knowsCounts': '{screens} screens · {controls} buttons it can point at · {rules} rules · {limits} known limits · {howtos} walkthroughs',
  'assistantAdmin.autosaves': 'There is nothing to press — changes save for the whole family as you make them.',
  'assistantAdmin.saving': 'Saving…',
  'assistantAdmin.saveFailed': 'Not saved: {why}',
  'assistantAdmin.savedForFamily': 'Saved for everyone',
  'assistantAdmin.saved': 'Saved.',

  // ---- recipes: the family's kitchen, kept under the circle of whoever cooks it ----
  'add.recipe': 'Write down a recipe',
  'add.recipeHelp': 'Say it out loud, or type it.',
  'recipe.one': 'Recipe',
  'recipe.add': 'Write down a recipe',
  'recipe.untitled': 'A recipe with no name yet',
  'recipe.draft': 'Draft',
  'recipe.draft.onlyYou': 'Only you can see this until you publish it.',
  'recipe.continue': 'Carry on',
  'recipe.ingredients': 'Ingredients',
  'recipe.method': 'Method',
  'recipe.notes': 'Notes',
  'recipe.step': 'Step',
  'recipe.step.done': 'Done this one',
  'recipe.step.undo': 'Not done yet',
  'recipe.byline.of': '{name}’s recipe',
  'recipe.byline.writtenBy': 'Written down by {name}',
  'recipe.react': 'Respond to this recipe',
  'recipe.awake': 'Keep the screen on',
  'recipe.awake.on': 'Screen stays on',
  'recipe.edit': 'Edit recipe',
  'recipe.editAsAdmin': 'Edit as administrator',
  'recipe.changes.waiting': 'You have changes to this recipe that the family cannot see yet.',
  'recipe.changes.note': 'The family still sees the recipe as it was. Your changes go out when you press Publish changes.',
  'recipe.studio.new': 'New recipe',
  'recipe.studio.editing': 'Editing recipe',
  'recipe.status.saving': 'Saving…',
  'recipe.status.draft': 'Saved — only you can see it',
  'recipe.status.changes': 'Saved — not published yet',
  'recipe.status.published': 'Published',
  'recipe.status.failed': 'Not saved yet',
  'recipe.publish': 'Publish',
  'recipe.publishChanges': 'Publish changes',
  'recipe.publishing': 'Publishing…',
  'recipe.published': 'Shared with the family',
  'recipe.changesPublished': 'Your changes are out',
  'recipe.keptAsDraft': 'Kept as a draft — it is waiting on {name}’s circle',
  'recipe.tell.title': 'Tell me the recipe',
  'recipe.tell.titleFor': 'Tell me {name}’s recipe',
  'recipe.tell.help': 'Say it the way you would across the kitchen table — what goes in, how much, and what you do. In any order. I will write it up properly.',
  'recipe.tell.helpNoModel': 'Say it the way you would across the kitchen table. No assistant is connected, so your words will be laid out as they are, for you to tidy.',
  'recipe.tell.typeHelp': 'Type it or paste it, as roughly as you like.',
  'recipe.tell.typePlaceholder': 'Savta’s chicken soup. One whole chicken, three carrots…',
  'recipe.tell.start': 'Start telling the recipe',
  'recipe.tell.done': 'I have finished',
  'recipe.tell.pressWhenDone': 'Take your time. Press the tick when you have finished.',
  'recipe.tell.listening': 'Listening…',
  'recipe.tell.listeningBlind': 'Listening. Your words will appear when you finish.',
  'recipe.tell.reading': 'Just a moment…',
  'recipe.tell.useKeyboard': 'Type it instead',
  'recipe.tell.useVoice': 'Say it instead',
  'recipe.tell.startOver': 'Start again',
  'recipe.tell.writeUp': 'Write it up',
  'recipe.tell.layOut': 'Lay it out',
  'recipe.tell.asIs': 'Use my words as they are',
  'recipe.tell.heardNothing': 'Nothing was heard. Press the microphone and try again — or type it.',
  'recipe.tell.stopped': 'The microphone stopped by itself. Here is what it heard — add anything missing, then press Write it up.',
  'recipe.tell.noRecipe': 'There was no recipe in that. Say what the dish is, what goes in it and what you do.',
  'recipe.writing.title': 'Writing up your recipe…',
  'recipe.writing.help': 'Sorting what goes in from what you do. Nothing is being added to what you said.',
  'recipe.change.hint': 'Say what to change — “line 5 should be two cups of sugar”. Or tap any line.',
  'recipe.change.hintType': 'Type what to change — “line 5 should be two cups of sugar”. Or tap any line.',
  'recipe.change.hintNoModel': 'Tap any line to change it.',
  'recipe.change.start': 'Say what to change',
  'recipe.change.send': 'Send',
  'recipe.change.type': 'Type what to change',
  'recipe.change.placeholder': 'Line 5 should be two cups of sugar',
  'recipe.change.listening': 'Listening — what should change?',
  'recipe.change.thinking': 'Changing it…',
  'recipe.ai.changed': 'Done — the lit lines are what changed.',
  'recipe.ai.unchanged': 'Nothing was changed. Try saying which line, and what it should say.',
  'recipe.ai.laidOut': 'Your words, laid out as they are. Tap any line to tidy it.',
  'recipe.ai.noModel': 'No assistant is connected, so your words were laid out as they are. Tap any line to tidy it.',
  'recipe.undo': 'Undo the last change',
  'recipe.line.label': 'Line {n}: {text}',
  'recipe.line.remove': 'Remove line',
  'recipe.add.intro': 'A line about this dish',
  'recipe.add.fact': 'Serves, time…',
  'recipe.add.ingredient': 'Add an ingredient',
  'recipe.add.step': 'Add a step',
  'recipe.add.note': 'Add a note',
  'recipe.placeholder.title': 'The name of the dish',
  'recipe.placeholder.intro': 'Whose dish it is, when it is made…',
  'recipe.placeholder.fact': 'Serves 6',
  'recipe.placeholder.ingredient': '2 cups flour  (end with : for a heading)',
  'recipe.placeholder.step': 'What to do next',
  'recipe.placeholder.note': 'A tip, a variation, how it keeps',
  'recipe.who.ask': 'Whose recipe is it?',
  'recipe.who.help': 'It will hang on their circle in the tree. Choose yourself, or the person whose recipe you are writing down.',
  'recipe.who.change': 'Change',
  'recipe.photo.failed': 'That picture could not be added.',
  // Its pictures. Anybody in the family can add one; the first stands for the recipe until its keeper chooses another.
  'recipe.pictures': 'Pictures',
  'recipe.pictures.invite': 'Made it? Add a picture — the whole family will see it.',
  'recipe.pictures.add': 'Add a picture',
  'recipe.pictures.addFirst': 'Add a picture of the dish',
  'recipe.pictures.adding': 'Adding…',
  'recipe.pictures.added': 'Picture added',
  'recipe.pictures.addedMany': '{n} pictures added',
  'recipe.pictures.onlyPictures': 'Only pictures can be added to a recipe.',
  'recipe.pictures.pasteHint': 'You can also copy a picture and paste it here (Ctrl+V).',
  'recipe.pictures.open': 'Picture {n}',
  'recipe.pictures.openMain': 'Open the picture',
  'recipe.pictures.main': 'Main picture',
  'recipe.pictures.makeMain': 'Make this the main picture',
  'recipe.pictures.madeMain': 'This is now the recipe’s main picture',
  'recipe.deleteDraft': 'Delete this draft',
  'recipe.deleteDraft.body': 'Nobody else has seen it, so it will simply be gone.',
  'recipe.discardChanges': 'Discard my changes',
  'recipe.discardChanges.body': 'The recipe goes back to the way the family sees it now.',
  'recipe.takeDown': 'Take this recipe down',
  'recipe.takeDown.body': 'The family will no longer see it. It is kept, and an administrator can put it back.',
  'recipe.draftDeleted': 'Draft deleted',
  'recipe.changesDiscarded': 'Changes discarded',
  'recipe.takenDown': 'Recipe taken down',
  'recipe.clash.title': 'This recipe was changed somewhere else',
  'recipe.clash.body': 'On another device, or by somebody else who may edit it. Which one should be kept?',
  'recipe.clash.mine': 'Keep mine',
  'recipe.clash.theirs': 'Use the other one',
  'recipe.error.notFound': 'That recipe is no longer here.',
  'recipe.error.needTitle': 'Give the dish a name first.',
  'recipe.error.needBody': 'Add at least one ingredient or one step first.',
  'recipe.error.needPerson': 'Choose whose recipe it is first.',
  'recipe.error.notYours': 'Only whoever wrote this recipe down, the person it belongs to, or an administrator can change it.',

  // ---- Passed Down: the shelf of what the family hands on (recipes today; more kinds to come) ----
  'passed.search': 'Search what has been passed down',
  'passed.search.hint': 'Search — a dish, an ingredient, a name',
  'passed.search.clear': 'Clear the search',
  'passed.kinds': 'Show one type',
  'passed.kind.recipes': 'Recipes',
  'passed.found': '{count} found',
  'passed.showAll': 'Show everything',
  'passed.none': 'Nothing matches that. Try another word, or show everything.',
  'passed.empty.title': 'Nothing has been passed down yet',
  'passed.empty.help': 'Start with the recipe everybody asks for: press + at the bottom and choose “Write down a recipe”. Say it out loud and it is written up for you — and from then on the whole family has it, for good.',

  // ---- The welcome message: the billboard everybody reads once, and again when it changes (domain/welcome.ts) ----
  'greeting.menu': 'Welcome message',
  'issue.menu': 'Report a problem',
  'issue.title': 'Report a problem or an idea',
  'issue.kind': 'What is it?',
  'issue.kind.fault': 'Something is wrong',
  'issue.kind.idea': 'An idea',
  'issue.kind.question': 'A question',
  'issue.what': 'In one line',
  'issue.what.placeholder.fault': 'e.g. The photo did not save',
  'issue.what.placeholder.idea': 'e.g. Show birthdays on the tree',
  'issue.what.placeholder.question': 'e.g. How do I add my grandmother?',
  'issue.detail': 'More details (optional)',
  'issue.detail.placeholder': 'What did you do, and what happened?',
  'issue.help': 'Which screen you are on and which phone or computer you use are sent with it, so you do not have to explain.',
  'issue.send': 'Send',
  'issue.sending': 'Sending…',
  'issue.close': 'Close',
  'issue.thanks': 'Thank you — the administrators have it.',
  'issue.thanksGithub': 'Thank you — it is with the administrators and the developers (#{n}).',
  'issue.error': 'It could not be sent. Check the connection and try again.',
  'greeting.continue': 'Continue',
  'greeting.changedNote': 'This message has changed since you last read it.',
  'greeting.previewNote': 'A preview — only you can see this.',
  // The app's own welcome, shown until an administrator writes another. A blank line is a new paragraph.
  'greeting.default': 'This is our family’s own place — our people, our stories and our recipes, kept together so that none of it is lost.\n\nStart with the tree: tap anyone’s circle to see their pictures, hear their stories and find their recipes.\n\nThe + at the bottom is how you add something of your own — a photo, a story told out loud, a recipe everybody asks for.\n\nIf you are ever unsure what to do, press the microphone in the corner and ask. It answers in plain words and can show you where to press.\n\nEverything here is private to the family.',
  'greeting.error.empty': 'Write something in at least one language first.',
  'manage.section.welcome': 'Welcome message',
  'greeting.admin.help': 'This is what everyone reads the first time they open the app. When you save a change it comes up once more for everyone — so it is also a way to tell the whole family something. Anyone can read it again from the menu.',
  'greeting.admin.en': 'In English',
  'greeting.admin.he': 'In Hebrew',
  'greeting.admin.preview': 'See how it looks',
  'greeting.admin.oneIsEnough': 'One language is enough: whoever reads the other is shown this one instead. A blank line starts a new paragraph.',
  'greeting.admin.onlyEn': 'Only the English has changed. Hebrew readers will still be shown the Hebrew text — change it as well, or empty it and they will read the English.',
  'greeting.admin.onlyHe': 'Only the Hebrew has changed. English readers will still be shown the English text — change it as well, or empty it and they will read the Hebrew.',
  'greeting.admin.save': 'Save for everyone',
  'greeting.admin.saved': 'Saved. Everyone will see the new welcome message the next time they open the app.',
  'greeting.admin.willShow': 'When you save, everyone in the family will be shown this once more.',
  'greeting.admin.changedBy': 'Last changed by {name} · {when}',
  'greeting.admin.builtIn': 'The family is reading the app’s own welcome. Nobody has changed it yet.',
  'greeting.admin.original': 'Put back the original wording',

  // ---- Admin controls: a menu, and a page for each thing in it (features/admin/sections.ts) ----
  'manage.helpEditor': 'What the family has asked to change, what they have reported, and who has been invited. Whatever is done here is kept in the history.',
  'manage.backToMenu': 'Back to {title}',
  'manage.waiting': '{n} waiting',
  'manage.group.todo': 'Waiting for you',
  'manage.group.access': 'Who comes in',
  'manage.group.care': 'Looking after what is here',
  'manage.group.family': 'For the whole family',
  'manage.group.prototype': 'Prototype',
  'manage.section.people': 'New people & circles',
  'manage.about.review': 'Approve, reject or ask about requested changes.',
  'manage.about.people': 'Approve newcomers; remove or restore a circle.',
  'manage.about.reports': 'Faults, ideas and questions from the Help button.',
  'manage.about.duplicates': 'Two circles that may be the same person.',
  'manage.about.jobs': 'Recordings that failed to upload or back up.',
  'manage.about.access': 'Invite somebody, and the codes that let people in.',
  'manage.about.admins': 'Who runs the family, and votes waiting for you.',
  'manage.about.roles': 'What each account may do, and who helps whom.',
  'manage.about.moderation': 'Lock, take down or put back a story or recipe.',
  'manage.about.locks': 'Circles and details protected from changes.',
  'manage.about.decisions': 'What each administrator did, when and why.',
  'manage.about.audit': 'Every change to the family, and undoing one.',
  'manage.about.welcome': 'What everyone reads when they first open the app.',
  'manage.about.tree': 'How circles and badges grow and settle.',
  'manage.about.assistant': 'Who answers the Help button, and its voice.',
  'manage.about.prototype': 'Simulate being offline, failures, other people.',
  'manage.tree.save': 'Save',
  'manage.tree.unsaved': 'Unsaved changes to the tree display',
  // ---- the review queue ----
  'review.empty': 'Nothing is waiting for review.',
  'review.showSettled': 'Show the {n} already dealt with',
  'review.hideSettled': 'Only what is waiting',
  'review.asked': 'Sent. They will see your question.',
  'review.askSend': 'Send the question',
  'review.askLabel': 'Your question for {name}',
  'review.rejectHelp': 'Nothing in the tree changes. They are told, with whatever you write here.',
  // ---- possible duplicates ----
  'dup.adminOnly': 'Only an administrator can merge two people. You can still say when two are not the same person.',
  'dup.empty': 'No possible duplicates right now.',
  'dup.dismissed': 'Noted — they are two different people.',
  'dup.preview.nothing': 'Nothing else is attached to the second record, so nothing has to move.',
  // ---- new people & circles ----
  'people.waiting': 'Waiting to be recognised',
  'people.waiting.none': 'Nobody is waiting. Everyone in the tree has been recognised.',
  'people.waiting.count': '{n} of {needed} relatives have said they know them',
  'people.removal.title': 'Removing a circle',
  'people.removal.help': 'Taking a circle out of the tree is never one administrator’s decision while others are around: it needs a majority of the administrators who have been active lately. Nothing is destroyed.',
  'people.removal.start': 'Propose removing a circle',
  'people.removal.who': 'Whose circle?',
  'people.removal.needs': 'Right now it takes {n} of the {total} administrators who have been active lately. Proposing it counts as your own yes.',
  'people.removal.alone': 'You are the only administrator who has been active lately, so this goes ahead as soon as you propose it. It can be put back from “Removed from the tree”.',
  'people.removed': 'Removed from the tree',
  'people.removed.when': 'Removed {when}',
  'people.removed.back': '{name} is back in the tree.',
  'people.removed.putBack': 'Put back in the tree',
  'people.find': 'Somebody in particular',
  'people.find.help': 'Find anyone to change who looks after their circle, edit their details, or propose removing them.',
  'people.find.profile': 'Open their profile',
  // ---- invitations & codes ----
  'access.copy': 'Copy',
  'access.copied': 'Copied.',
  'access.copyFailed': 'Could not copy. Press and hold the code to copy it yourself.',
  'access.send': 'Send',
  'access.copyMessage': 'Copy a message',
  'access.copiedMessage': 'A message with the code is copied — paste it to them.',
  'access.invites.created': 'Invitation ready. Send them the code.',
  'access.temp.issued': 'Device code ready.',
  'access.codes.created': 'New family code ready.',
  'access.revoked': 'Revoked. It no longer works.',
  'access.invites.codeLabel': 'Invitation code',
  'access.temp.codeLabel': 'Device code',
  'access.codes.codeLabel': 'Family access code',
  'access.share.invite': '{name}, this is your invitation to our family tree.\n\nOpen {url}\nChoose “{choice}” and enter this code:\n\n{code}',
  'access.share.temp': 'To open our family tree on {device}:\n\nOpen {url}\nChoose “{choice}” and enter this code:\n\n{code}',
  'access.share.code': 'Come and join our family tree.\n\nOpen {url}\nChoose “{choice}” and enter this code:\n\n{code}',
  'access.finished': 'Used, expired or revoked',
  'access.temp.redeemed': 'First used {when}',
  'access.codes.create': 'Create code',
  'access.invites.formHelp': 'An invitation belongs to one person in the tree, works once, and lasts 14 days. When they use it, their account is connected to their own circle.',
  'access.invites.destinationHelp': 'Their email address or phone number. You can also send them the code yourself, once it is made.',
  'access.temp.formHelp': 'For somebody with no email and no phone. The code signs one device in as them; they can choose a simple PIN afterwards.',
  'access.temp.devicePlaceholder': 'Living-room tablet',
  'access.codes.formHelp': 'A family code only opens the door — whoever uses it still signs in as themselves, and asks to be connected to their circle. It works for up to 50 people.',
  'access.codes.labelPlaceholder': 'Family reunion',
  'access.validFor': 'Works for',
  'access.revoke.title': 'Revoke {what}?',
  'access.revoke.invite': 'The invitation code stops working. You can always make them a new one.',
  'access.revoke.temp': 'That device is signed out the next time it is opened, and the code stops working. Their recordings and everything they added stay.',
  'access.revoke.code': 'Nobody new can come in with this code. People who already joined with it are not affected.',
  // ---- administrators ----
  'admins.rulesUndo': 'Put back',
  // ---- accounts & helpers ----
  'roles.accounts': 'Accounts',
  'roles.signedInAs': 'Signed in as {name}',
  'roles.notConnected': 'Not connected to anyone in the tree yet',
  'roles.adminByAgreement': 'Administrators are chosen by agreement',
  'roles.changed': '{name} is now: {role}.',
  'roles.explain': 'A Family Editor can approve the family’s changes, answer reports and send invitations. A Member adds and suggests. Administrators are not chosen here — that list changes only when the administrators agree.',
  'roles.helpers.none': 'Nobody is helping with anyone else’s profile yet.',
  'roles.helpedBy': 'Helped by {name}',
  'roles.granted': 'Done. They can help from now on.',
  'roles.grant.choose': 'Choose an account…',
  'roles.disconnected': 'Disconnected. The circle and everything on it stay.',
  'roles.disconnect.short': 'Disconnect from this circle',
  'roles.disconnect.body': '{account} will no longer be signed in as {name}. This is for when somebody claimed the wrong person. The circle, its stories and its pictures all stay, and the account can be connected to the right person afterwards.',
  'roles.disconnect.confirm': 'Disconnect',
  'roles.revoked': 'Removed. They can no longer help with that profile.',
  'roles.revoke.title': 'Stop this helper?',
  'roles.revoke.body': '{helper} will no longer be able to manage {name}’s profile. What they already added stays.',
  'roles.error.adminByAgreement': 'Administrators change only by agreement. Open Administrators to suggest one, or to step down.',
  // ---- locked records ----
  'locks.add': 'Lock a circle',
  'locks.none': 'Nothing is locked.',
  'locks.people': 'Locked circles',
  'locks.connections': 'Locked connections',
  'locks.stories': 'Locked stories',
  'locks.rel.partner': 'Partners',
  'locks.rel.parent_child': 'Parent and child',
  'locks.unlocked': '{name} is unlocked.',
  'locks.unlockedPlain': 'Unlocked.',
  'locks.locked': '{name} is locked. Changes will come to the administrators as suggestions.',
  'locks.who': 'Whose circle?',
  'locks.what': 'How much of it?',
  'locks.whole': 'The whole circle',
  'locks.whole.help': 'For somebody who has passed away, or cannot join yet. Every change becomes a suggestion.',
  'locks.some': 'Some details only',
  'locks.some.help': 'For a name or a date checked against the family’s records.',
  'locks.reasonPlaceholder': 'Checked against the family record book',
  'locks.field.names': 'Names',
  'locks.field.birth': 'Birth date',
  'locks.field.death': 'Date of passing',
  'locks.field.status': 'Living or passed away',
  'locks.field.gender': 'Gender',
  // ---- stories & recipes ----
  'moderation.search': 'Search stories and recipes',
  'moderation.filter.locked': 'Locked',
  'moderation.filter.down': 'Taken down',
  'moderation.noneDown': 'Nothing has been taken down.',
  'moderation.none': 'Nothing matches that.',
  'moderation.restored': 'Back where the family can see it.',
  'moderation.lockedNow': 'Locked. Changes to it now come as suggestions.',
  'moderation.takenDown': 'Taken down. It is kept, and can be put back from here.',
  'moderation.archive.title': 'Take this story down?',
  'moderation.archive.body': 'The family stops seeing it. The recording and everything about it are kept, and any administrator can put it back from here.',
  // ---- decisions, uploads, history ----
  'decisions.emptyRemovals': 'No picture has been removed.',
  'decisions.newestFirst': 'Newest first',
  'jobs.none': 'Nothing is stuck. Every recording has uploaded and been backed up.',
  'audit.none': 'Nothing recorded yet.',
  'audit.putBack': 'Put it back',
  'audit.restore.body': 'This makes a new change that puts things back as they were. The history keeps both, so this can be undone too.',
} as const;

export type MessageKey = keyof typeof en;
